import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { BACKEND_URL } from '../config/canonical';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertTriangle, Clock, RefreshCw, ChevronRight, Plus, Edit3, Trash2, Archive,
  PowerOff, Power, KeyRound, Send, Mail, FileUp, CheckCircle2, XCircle, Eye,
  MessageSquare, ShieldAlert, UserPlus, Download, ClipboardList, FileText,
  Sparkles, Award, Copy,
} from 'lucide-react';

// ------------------------------ constants -----------------------------------
const STAGE_META = [
  { key: 'APPLIED',              label: 'Applied',              tone: 'bg-slate-200 text-slate-900 border-slate-400' },
  { key: 'UNDER_REVIEW',         label: 'Under Review',         tone: 'bg-blue-200 text-blue-900 border-blue-400' },
  { key: 'APPROVED_FOR_TESTING', label: 'Approved for Testing', tone: 'bg-indigo-200 text-indigo-900 border-indigo-400' },
  { key: 'TEST_IN_PROGRESS',     label: 'Test in Progress',     tone: 'bg-amber-200 text-amber-900 border-amber-400' },
  { key: 'AWAITING_TEST_REVIEW', label: 'Awaiting Test Review', tone: 'bg-amber-300 text-amber-950 border-amber-500' },
  { key: 'INTERVIEW_PENDING',    label: 'Interview Pending',    tone: 'bg-purple-200 text-purple-900 border-purple-400' },
  { key: 'INTERVIEW_SCHEDULED',  label: 'Interview Scheduled',  tone: 'bg-purple-300 text-purple-950 border-purple-500' },
  { key: 'INTERVIEW_COMPLETED',  label: 'Interview Completed',  tone: 'bg-fuchsia-200 text-fuchsia-900 border-fuchsia-400' },
  { key: 'PASSED_INTERVIEW',     label: 'Passed Interview',     tone: 'bg-emerald-200 text-emerald-900 border-emerald-400' },
  { key: 'FAILED_INTERVIEW',     label: 'Failed Interview',     tone: 'bg-rose-200 text-rose-900 border-rose-400' },
  { key: 'AWAITING_DOCUMENTS',   label: 'Awaiting Documents',   tone: 'bg-cyan-200 text-cyan-900 border-cyan-400' },
  { key: 'DOCUMENT_REVIEW',      label: 'Document Review',      tone: 'bg-cyan-300 text-cyan-950 border-cyan-500' },
  { key: 'FINAL_DECISION',       label: 'Final Decision',       tone: 'bg-yellow-300 text-yellow-950 border-yellow-500' },
  { key: 'HIRED',                label: 'Hired',                tone: 'bg-emerald-600 text-white border-emerald-700' },
  { key: 'NOT_HIRED',            label: 'Not Hired',            tone: 'bg-slate-700 text-white border-slate-800' },
  { key: 'ARCHIVED',             label: 'Archived',             tone: 'bg-slate-400 text-slate-900 border-slate-500' },
];

// 5-stage consolidated pipeline. Each bucket carries:
//   - the granular keys it bundles (used for label lookup + filtering)
//   - the canonical key we PATCH to the backend when an admin moves the
//     applicant INTO this bucket (chosen as the "entry stage" for the bucket)
const STAGE_BUCKETS = [
  { id: 'APPLIED_REVIEW',  label: 'Applied / Under Review',           short: 'Applied',     dot: 'bg-slate-400',    tone: 'bg-slate-200 text-slate-900 border-slate-400',         entry: 'APPLIED',              keys: ['APPLIED', 'UNDER_REVIEW'] },
  { id: 'TESTING',         label: 'Testing Phase',                    short: 'Testing',     dot: 'bg-amber-500',    tone: 'bg-amber-200 text-amber-900 border-amber-400',         entry: 'APPROVED_FOR_TESTING', keys: ['APPROVED_FOR_TESTING', 'TEST_IN_PROGRESS', 'AWAITING_TEST_REVIEW'] },
  { id: 'INTERVIEW',       label: 'Interview Phase',                  short: 'Interview',   dot: 'bg-purple-500',   tone: 'bg-purple-200 text-purple-900 border-purple-400',      entry: 'INTERVIEW_PENDING',    keys: ['INTERVIEW_PENDING', 'INTERVIEW_SCHEDULED', 'INTERVIEW_COMPLETED', 'PASSED_INTERVIEW', 'FAILED_INTERVIEW'] },
  { id: 'BACKGROUND_DOCS', label: 'Background & Document Verification', short: 'Docs',     dot: 'bg-cyan-500',     tone: 'bg-cyan-200 text-cyan-900 border-cyan-400',            entry: 'AWAITING_DOCUMENTS',   keys: ['AWAITING_DOCUMENTS', 'DOCUMENT_REVIEW', 'FINAL_DECISION'] },
  { id: 'HIRED_ONBOARDED', label: 'Hired / Onboarded',                short: 'Hired',       dot: 'bg-emerald-500',  tone: 'bg-emerald-200 text-emerald-900 border-emerald-400',   entry: 'HIRED',                keys: ['HIRED'] },
];
const TERMINAL_BUCKET = { id: 'TERMINAL', label: 'Closed', dot: 'bg-slate-500', tone: 'bg-slate-300 text-slate-900 border-slate-500', keys: ['NOT_HIRED', 'ARCHIVED'] };

const bucketForStage = (stage) => {
  if (!stage) return STAGE_BUCKETS[0];
  if (TERMINAL_BUCKET.keys.includes(stage)) return TERMINAL_BUCKET;
  return STAGE_BUCKETS.find(b => b.keys.includes(stage)) || STAGE_BUCKETS[0];
};
const granularLabel = (stage) => (STAGE_META.find(s => s.key === stage)?.label) || (stage || '').replace(/_/g, ' ');

const NOTICE_TEMPLATES = [
  { v: 'application_link',         label: 'Application link' },
  { v: 'temp_password',            label: 'Temporary password notice' },
  { v: 'password_reset',           label: 'Password reset notice' },
  { v: 'interview_invite',         label: 'Interview invitation' },
  { v: 'interview_reminder',       label: 'Interview reminder' },
  { v: 'psych_test_notice',        label: 'Psychological test notice' },
  { v: 'status_update',            label: 'Hiring status update' },
  { v: 'approval',                 label: 'Approval notice' },
  { v: 'denial',                   label: 'Denial notice' },
  { v: 'onboarding_instructions',  label: 'Onboarding instructions' },
  { v: 'document_request',         label: 'Document signing request' },
  { v: 'credential_upload_request',label: 'Credential upload request' },
  { v: 'handbook_delivery',        label: 'Handbook delivery' },
  { v: 'policy_acknowledgment',    label: 'Policy acknowledgment' },
  { v: 'custom',                   label: 'Custom message (define below)' },
];

// ----------------------------- api helper -----------------------------------
const useAuth = () => useMemo(() => {
  const token = localStorage.getItem('nexus_token');
  const api = async (path, opts = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      ...opts,
      headers: {
        ...(opts.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${token}`,
        ...(opts.headers || {}),
      },
    });
    let data = null;
    try { data = await res.json(); } catch { /* non-json */ }
    if (!res.ok) throw new Error(data?.detail || `Request failed (${res.status})`);
    return data;
  };
  return { token, api };
}, []);

const genNexusUsername = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let s = 'NEXUS-';
  for (let i = 0; i < 4; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
};
const normalizeNexusUsername = (v) => (v || '').trim().toUpperCase();
const isValidNexusUsername = (v) => /^NEXUS-[A-Z0-9]{4,8}$/.test(normalizeNexusUsername(v));

// ========================== Create Applicant Dialog =========================
const CreateApplicantDialog = ({ open, onClose, onCreated }) => {
  const { api } = useAuth();
  const [f, setF] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    position_applied: 'Field Investigator', stage: 'APPLIED', notes: '',
    portal_username: '',
    send_application_link: false, send_temp_password_email: true,
    send_test_notice: false, create_portal_account: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => { if (open) { setF(prev => ({ ...prev, portal_username: genNexusUsername() })); } }, [open]);
  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || !f.phone) {
      toast.error('Name, email and phone are required'); return;
    }
    const uname = normalizeNexusUsername(f.portal_username);
    if (f.create_portal_account && uname && !isValidNexusUsername(uname)) {
      toast.error('Username must match NEXUS-XXXX (4–8 alphanumerics)'); return;
    }
    setBusy(true);
    try {
      const payload = { ...f, portal_username: uname || null };
      const data = await api('/api/admin/applicants', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`Applicant ${data.applicant.id} created`);
      if (data.temp_password) {
        toast.message(`Username: ${data.applicant.portal_username}`, { description: `Temp password: ${data.temp_password}`, duration: 12000 });
      }
      onCreated?.(data.applicant);
      onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="create-applicant-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Create applicant portal account</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-800">First name *</Label><Input value={f.first_name} onChange={e=>set('first_name',e.target.value)} data-testid="ca-first" /></div>
          <div><Label className="text-slate-800">Last name *</Label><Input value={f.last_name} onChange={e=>set('last_name',e.target.value)} data-testid="ca-last" /></div>
          <div><Label className="text-slate-800">Email *</Label><Input type="email" value={f.email} onChange={e=>set('email',e.target.value)} data-testid="ca-email" /></div>
          <div><Label className="text-slate-800">Phone *</Label><Input value={f.phone} onChange={e=>set('phone',e.target.value)} data-testid="ca-phone" /></div>
          <div><Label className="text-slate-800">Position</Label><Input value={f.position_applied} onChange={e=>set('position_applied',e.target.value)} /></div>
          <div>
            <Label className="text-slate-800">Initial stage</Label>
            <Select value={f.stage} onValueChange={v=>set('stage',v)}>
              <SelectTrigger data-testid="ca-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGE_META.map(s=>(<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-slate-800">Portal username (NEXUS-XXXX)</Label>
            <div className="flex gap-2">
              <Input value={f.portal_username} onChange={e=>set('portal_username', e.target.value.toUpperCase())} placeholder="NEXUS-ABCD" className="font-mono" data-testid="ca-username" />
              <Button type="button" variant="outline" onClick={() => set('portal_username', genNexusUsername())} className="border-slate-300 !text-slate-900 whitespace-nowrap" data-testid="ca-username-gen">Auto-generate</Button>
            </div>
            {f.portal_username && !isValidNexusUsername(f.portal_username) && (
              <p className="text-[11px] text-rose-700 mt-1">Must match NEXUS-XXXX (4–8 letters/digits)</p>
            )}
          </div>
          <div className="col-span-2"><Label className="text-slate-800">Internal notes</Label><Textarea rows={2} value={f.notes} onChange={e=>set('notes',e.target.value)} /></div>
          <div className="col-span-2 border-t border-slate-200 pt-3 grid grid-cols-1 gap-2">
            {[
              ['create_portal_account', 'Create applicant portal login (with temp password)'],
              ['send_temp_password_email', 'Email the applicant their temp password immediately'],
              ['send_application_link', 'Also send the public application link'],
              ['send_test_notice', 'Also send the IPS-OIE psych test notice'],
            ].map(([k,l])=>(
              <label key={k} className="flex items-center gap-2 text-sm text-slate-800">
                <Checkbox checked={f[k]} onCheckedChange={v=>set(k,!!v)} data-testid={`ca-${k}`} />
                {l}
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="!text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="ca-submit">
            {busy ? 'Creating…' : 'Create account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================== Edit Dialog =================================
const EditApplicantDialog = ({ open, onClose, applicant, onSaved }) => {
  const { api } = useAuth();
  const [f, setF] = useState({});
  useEffect(() => { if (applicant) setF({ ...applicant }); }, [applicant]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  if (!applicant) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const keys = ['first_name','last_name','email','phone','portal_username','date_of_birth','address_line1','address_line2','city','state','postal_code','position_applied','drivers_license_number','drivers_license_state','pi_license_number','pi_license_state','license_expiration','assigned_reviewer_id','assigned_interviewer_id','interview_type','interview_outcome','onboarding_progress','risk_flag'];
      const patch = {};
      keys.forEach(k => { if (f[k] !== applicant[k] && f[k] !== undefined) patch[k] = f[k]; });
      if (patch.portal_username) {
        patch.portal_username = normalizeNexusUsername(patch.portal_username);
        if (!isValidNexusUsername(patch.portal_username)) {
          toast.error('Username must match NEXUS-XXXX (4–8 alphanumerics)'); setBusy(false); return;
        }
      }
      const data = await api(`/api/admin/applicants/${applicant.id}`, { method: 'PATCH', body: JSON.stringify(patch) });
      toast.success('Applicant updated');
      onSaved?.(data.applicant);
      onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const field = (k, label, type='text') => (
    <div><Label className="text-slate-800">{label}</Label>
      <Input type={type} value={f[k] ?? ''} onChange={e=>set(k, e.target.value)} data-testid={`edit-${k}`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={v=>!v && onClose?.()}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="edit-applicant-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Edit applicant — {applicant.id}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 bg-amber-50 border border-amber-300 rounded p-3">
            <Label className="text-slate-800">Portal username (NEXUS-XXXX)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={f.portal_username || ''} onChange={e=>set('portal_username', e.target.value.toUpperCase())} placeholder="NEXUS-ABCD" className="font-mono" data-testid="edit-portal_username" />
              <Button type="button" variant="outline" onClick={() => set('portal_username', genNexusUsername())} className="border-slate-300 !text-slate-900 whitespace-nowrap">Auto-generate</Button>
            </div>
            {f.portal_username && !isValidNexusUsername(f.portal_username) && (
              <p className="text-[11px] text-rose-700 mt-1">Must match NEXUS-XXXX (4–8 letters/digits)</p>
            )}
          </div>
          {field('first_name', 'First name')}
          {field('last_name', 'Last name')}
          {field('email', 'Email', 'email')}
          {field('phone', 'Phone')}
          {field('date_of_birth', 'Date of birth', 'date')}
          {field('position_applied', 'Position')}
          {field('address_line1', 'Address')}
          {field('address_line2', 'Apt / Suite')}
          {field('city', 'City')}
          {field('state', 'State')}
          {field('postal_code', 'ZIP')}
          {field('drivers_license_number', 'DL #')}
          {field('drivers_license_state', 'DL state')}
          {field('pi_license_number', 'PI license #')}
          {field('pi_license_state', 'PI license state')}
          {field('license_expiration', 'License expiration', 'date')}
          {field('assigned_reviewer_id', 'Assigned reviewer')}
          {field('assigned_interviewer_id', 'Assigned interviewer')}
          {field('interview_type', 'Interview type')}
          {field('interview_outcome', 'Interview outcome')}
          {field('onboarding_progress', 'Onboarding progress')}
          {field('risk_flag', 'Risk flag')}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="!text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="edit-submit">{busy ? 'Saving…' : 'Save changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================== Delete Dialog ===============================
const DeleteApplicantDialog = ({ open, onClose, applicant, onDeleted }) => {
  const { api } = useAuth();
  const [mode, setMode] = useState('archive'); // archive | soft | permanent
  const [reason, setReason] = useState('');
  const [purge, setPurge] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMode('archive'); setReason(''); setPurge(false); setConfirmText(''); } }, [open]);
  if (!applicant) return null;
  const submit = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (mode === 'permanent' && confirmText !== applicant.id) { toast.error(`Type the applicant ID to confirm: ${applicant.id}`); return; }
    setBusy(true);
    try {
      if (mode === 'archive') {
        await api(`/api/admin/applicants/${applicant.id}/archive`, { method: 'POST', body: JSON.stringify({ reason }) });
        toast.success('Applicant archived');
      } else if (mode === 'soft') {
        await api(`/api/admin/applicants/${applicant.id}/soft-delete`, { method: 'POST', body: JSON.stringify({ reason }) });
        toast.success('Applicant soft-deleted');
      } else {
        await api(`/api/admin/applicants/${applicant.id}`, { method: 'DELETE', body: JSON.stringify({ reason, purge_records: purge, confirm: true }) });
        toast.success('Applicant permanently deleted');
      }
      onDeleted?.(mode);
      onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v=>!v && onClose?.()}>
      <DialogContent className="bg-white" data-testid="delete-applicant-dialog">
        <DialogHeader><DialogTitle className="text-slate-900 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-rose-600" /> Remove applicant — {applicant.id}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            {[
              ['archive', 'Archive — keeps full records, removes from active pipeline'],
              ['soft', 'Soft delete — hides from active lists and locks portal access'],
              ['permanent', 'Permanent delete — removes record (audit log retained)'],
            ].map(([v,l])=>(
              <label key={v} className="flex items-start gap-2 text-sm text-slate-800">
                <input type="radio" checked={mode===v} onChange={()=>setMode(v)} data-testid={`del-mode-${v}`} className="mt-1" />
                <span>{l}</span>
              </label>
            ))}
          </div>
          <div><Label className="text-slate-800">Reason *</Label><Textarea rows={2} value={reason} onChange={e=>setReason(e.target.value)} data-testid="del-reason" /></div>
          {mode === 'permanent' && (
            <>
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <Checkbox checked={purge} onCheckedChange={v=>setPurge(!!v)} data-testid="del-purge" />
                Also purge messages + documents (audit log is retained)
              </label>
              <div><Label className="text-slate-800">Type the applicant ID to confirm: <span className="font-mono text-rose-700">{applicant.id}</span></Label>
                <Input value={confirmText} onChange={e=>setConfirmText(e.target.value)} data-testid="del-confirm" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="!text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="del-submit">
            {busy ? 'Working…' : (mode === 'permanent' ? 'Permanently delete' : mode === 'soft' ? 'Soft delete' : 'Archive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================ Send Message Dialog ===========================
const SendMessageDialog = ({ open, onClose, applicant, onSent }) => {
  const { api } = useAuth();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [delivery, setDelivery] = useState('both');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSubject(''); setBody(''); setDelivery('both'); } }, [open]);
  if (!applicant) return null;
  const submit = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body required'); return; }
    setBusy(true);
    try {
      await api(`/api/admin/applicants/${applicant.id}/messages`, { method: 'POST', body: JSON.stringify({ subject, body, delivery }) });
      toast.success('Message sent'); onSent?.(); onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v=>!v && onClose?.()}>
      <DialogContent className="bg-white max-w-xl" data-testid="send-message-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Send message — {applicant.first_name} {applicant.last_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-800">Subject</Label><Input value={subject} onChange={e=>setSubject(e.target.value)} data-testid="msg-subject" /></div>
          <div><Label className="text-slate-800">Body</Label><Textarea rows={6} value={body} onChange={e=>setBody(e.target.value)} data-testid="msg-body" /></div>
          <div><Label className="text-slate-800">Delivery</Label>
            <Select value={delivery} onValueChange={setDelivery}>
              <SelectTrigger data-testid="msg-delivery"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="portal">Portal inbox only</SelectItem>
                <SelectItem value="email">Email only</SelectItem>
                <SelectItem value="both">Portal + Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="!text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="msg-send">{busy ? 'Sending…' : 'Send'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================ Send Notice Dialog ============================
const SendNoticeDialog = ({ open, onClose, applicant, onSent }) => {
  const { api } = useAuth();
  const [template, setTemplate] = useState('interview_invite');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [delivery, setDelivery] = useState('both');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTemplate('interview_invite'); setCustomSubject(''); setCustomBody(''); setDelivery('both'); } }, [open]);
  if (!applicant) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { template, delivery };
      if (template === 'custom') { payload.custom_subject = customSubject; payload.custom_body = customBody; }
      const res = await api(`/api/admin/applicants/${applicant.id}/notice`, { method: 'POST', body: JSON.stringify(payload) });
      toast.success('Notice sent');
      if (res?.temp_password) toast.message('Temporary password', { description: res.temp_password, duration: 10000 });
      onSent?.(); onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v=>!v && onClose?.()}>
      <DialogContent className="bg-white max-w-xl" data-testid="send-notice-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Send templated notice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-800">Template</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger data-testid="notice-template"><SelectValue /></SelectTrigger>
              <SelectContent>{NOTICE_TEMPLATES.map(t=>(<SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          {template === 'custom' && (
            <>
              <div><Label className="text-slate-800">Subject</Label><Input value={customSubject} onChange={e=>setCustomSubject(e.target.value)} /></div>
              <div><Label className="text-slate-800">Body</Label><Textarea rows={5} value={customBody} onChange={e=>setCustomBody(e.target.value)} /></div>
            </>
          )}
          <div><Label className="text-slate-800">Delivery</Label>
            <Select value={delivery} onValueChange={setDelivery}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="portal">Portal inbox only</SelectItem>
                <SelectItem value="email">Email only</SelectItem>
                <SelectItem value="both">Portal + Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="!text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="notice-send">{busy?'Sending…':'Send notice'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================ Assign Document Dialog ========================
const AssignDocumentDialog = ({ open, onClose, applicant, onSent }) => {
  const { api } = useAuth();
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('general');
  const [dueDate, setDueDate] = useState('');
  const [required, setRequired] = useState(true);
  const [requireSig, setRequireSig] = useState(false);
  const [requireUpload, setRequireUpload] = useState(false);
  const [requireApproval, setRequireApproval] = useState(true);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLabel(''); setCategory('general'); setDueDate(''); setRequired(true); setRequireSig(false); setRequireUpload(false); setRequireApproval(true); setNotes(''); setFile(null); }}, [open]);
  if (!applicant) return null;
  const submit = async () => {
    if (!label.trim()) { toast.error('Label required'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('label', label);
      fd.append('category', category);
      fd.append('required', required);
      if (dueDate) fd.append('due_date', dueDate);
      fd.append('require_signature', requireSig);
      fd.append('require_upload_back', requireUpload);
      fd.append('require_admin_approval', requireApproval);
      if (notes) fd.append('notes', notes);
      if (file) fd.append('file', file);
      await api(`/api/admin/applicants/${applicant.id}/documents`, { method: 'POST', body: fd });
      toast.success('Document assigned');
      onSent?.(); onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v=>!v && onClose?.()}>
      <DialogContent className="bg-white max-w-xl" data-testid="assign-doc-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Assign document to {applicant.first_name} {applicant.last_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-slate-800">Label *</Label><Input value={label} onChange={e=>setLabel(e.target.value)} placeholder="e.g. Use of Force Agreement" data-testid="doc-label" /></div>
          <div><Label className="text-slate-800">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['general','hiring_agreement','policy','handbook','licensing','credential','use_of_force','confidentiality'].map(c=>(<SelectItem key={c} value={c}>{c.replace(/_/g,' ')}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-slate-800">Due date</Label><Input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} /></div>
          <div className="col-span-2"><Label className="text-slate-800">File (PDF/DOC/Image)</Label>
            <Input type="file" onChange={e=>setFile(e.target.files?.[0] || null)} data-testid="doc-file" />
          </div>
          <div className="col-span-2 space-y-1">
            {[
              ['required', required, setRequired, 'Required'],
              ['require_signature', requireSig, setRequireSig, 'Require e-signature'],
              ['require_upload_back', requireUpload, setRequireUpload, 'Require upload back from applicant'],
              ['require_admin_approval', requireApproval, setRequireApproval, 'Require admin approval after submission'],
            ].map(([id, val, setter, label]) => (
              <label key={id} className="flex items-center gap-2 text-sm text-slate-800"><Checkbox checked={val} onCheckedChange={v=>setter(!!v)} data-testid={`doc-${id}`} />{label}</label>
            ))}
          </div>
          <div className="col-span-2"><Label className="text-slate-800">Notes (visible to applicant)</Label><Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="!text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="doc-submit">{busy?'Assigning…':'Assign document'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================ Interview Dialogs =============================
const INTERVIEW_TYPES = [
  { v: 'VIDEO', label: 'Video call' },
  { v: 'IN_PERSON', label: 'In-person' },
  { v: 'PHONE', label: 'Phone' },
];

const toInputDateTime = (iso) => {
  if (!iso) return '';
  // Accept either "YYYY-MM-DDTHH:MM:SSZ" or ISO with timezone → produce the local datetime-local value
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const InterviewDialog = ({ open, onClose, applicant, editing, onSaved }) => {
  const { api } = useAuth();
  const [f, setF] = useState({
    interview_type: 'VIDEO',
    scheduled_at: '',
    duration_minutes: 45,
    location: '',
    interviewer_name: '',
    notes: '',
    send_invite_email: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setF({
        interview_type: editing.interview_type || 'VIDEO',
        scheduled_at: toInputDateTime(editing.scheduled_at),
        duration_minutes: editing.duration_minutes || 45,
        location: editing.location || '',
        interviewer_name: editing.interviewer_name || '',
        notes: editing.notes || '',
        send_invite_email: false,
      });
    } else {
      setF({
        interview_type: 'VIDEO',
        scheduled_at: '',
        duration_minutes: 45,
        location: '',
        interviewer_name: '',
        notes: '',
        send_invite_email: true,
      });
    }
  }, [open, editing]);

  const submit = async () => {
    if (!f.scheduled_at) { toast.error('Pick a date and time'); return; }
    setBusy(true);
    try {
      const payload = {
        interview_type: f.interview_type,
        scheduled_at: new Date(f.scheduled_at).toISOString(),
        duration_minutes: parseInt(f.duration_minutes, 10) || 45,
        location: f.location || null,
        interviewer_name: f.interviewer_name || null,
        notes: f.notes || null,
      };
      if (editing) {
        await api(`/api/admin/applicants/${applicant.id}/interviews/${editing.id}`, {
          method: 'PATCH', body: JSON.stringify(payload),
        });
        toast.success('Interview updated');
      } else {
        await api(`/api/admin/applicants/${applicant.id}/interviews`, {
          method: 'POST', body: JSON.stringify({ ...payload, send_invite_email: f.send_invite_email }),
        });
        toast.success(f.send_invite_email ? 'Interview scheduled — invite emailed' : 'Interview scheduled');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
      <DialogContent className="bg-white text-slate-900 max-w-xl" data-testid="interview-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">{editing ? 'Edit interview' : 'Schedule interview'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-800 text-xs font-mono uppercase">Interview type</Label>
            <Select value={f.interview_type} onValueChange={v => setF({ ...f, interview_type: v })}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="itv-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INTERVIEW_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-800 text-xs font-mono uppercase">When</Label>
              <Input type="datetime-local" value={f.scheduled_at} onChange={e => setF({ ...f, scheduled_at: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="itv-when" />
            </div>
            <div>
              <Label className="text-slate-800 text-xs font-mono uppercase">Duration (minutes)</Label>
              <Input type="number" min="15" value={f.duration_minutes} onChange={e => setF({ ...f, duration_minutes: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="itv-duration" />
            </div>
          </div>
          <div>
            <Label className="text-slate-800 text-xs font-mono uppercase">
              {f.interview_type === 'VIDEO' ? 'Meeting URL' : f.interview_type === 'PHONE' ? 'Phone number' : 'Location / address'}
            </Label>
            <Input value={f.location} onChange={e => setF({ ...f, location: e.target.value })} placeholder={f.interview_type === 'VIDEO' ? 'https://meet.nexustrac.org/...' : ''} className="bg-white border-slate-300 text-slate-900" data-testid="itv-location" />
          </div>
          <div>
            <Label className="text-slate-800 text-xs font-mono uppercase">Interviewer name</Label>
            <Input value={f.interviewer_name} onChange={e => setF({ ...f, interviewer_name: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="itv-interviewer" />
          </div>
          <div>
            <Label className="text-slate-800 text-xs font-mono uppercase">Notes (for applicant)</Label>
            <Textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} className="bg-white border-slate-300 text-slate-900" data-testid="itv-notes" />
          </div>
          {!editing && (
            <label className="flex items-center gap-2 text-sm text-slate-800">
              <Checkbox checked={f.send_invite_email} onCheckedChange={v => setF({ ...f, send_invite_email: !!v })} data-testid="itv-send-invite" />
              Send branded invite email to applicant now
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-300 text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="itv-submit">
            {busy ? 'Saving…' : (editing ? 'Save changes' : 'Schedule interview')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InterviewOutcomeDialog = ({ open, onClose, applicant, interview, onSaved }) => {
  const { api } = useAuth();
  const [outcome, setOutcome] = useState('PASS');
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setOutcome('PASS'); setNotes(''); setSendEmail(true); } }, [open]);

  const submit = async () => {
    if (!interview?.id) return;
    setBusy(true);
    try {
      await api(`/api/admin/applicants/${applicant.id}/interviews/${interview.id}/outcome`, {
        method: 'POST',
        body: JSON.stringify({ outcome, decision_notes: notes || null, send_result_email: sendEmail }),
      });
      toast.success(`Interview marked ${outcome}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
      <DialogContent className="bg-white text-slate-900 max-w-md" data-testid="interview-outcome-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Record interview outcome</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-800 text-xs font-mono uppercase">Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="outcome-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PASS">Pass → Advance to documents stage</SelectItem>
                <SelectItem value="FAIL">Fail → Mark not hired</SelectItem>
                <SelectItem value="HOLD">Hold → Reviewer follow-up</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-800 text-xs font-mono uppercase">Decision notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="bg-white border-slate-300 text-slate-900" data-testid="outcome-notes" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-800">
            <Checkbox checked={sendEmail} onCheckedChange={v => setSendEmail(!!v)} data-testid="outcome-send-email" />
            Send branded result email to applicant
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-300 text-slate-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="outcome-submit">
            {busy ? 'Saving…' : 'Save outcome'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



// =============================== Applicant Detail ============================
const ApplicantDetail = ({ applicant, onClose, onChanged, initialTab }) => {
  const { api } = useAuth();
  const [tab, setTab] = useState(initialTab || 'overview');
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [testReview, setTestReview] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testOverride, setTestOverride] = useState({ band: '', recommendation: '', manual_notes: '' });
  const [interviews, setInterviews] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editInterview, setEditInterview] = useState(null);
  const [outcomeInterview, setOutcomeInterview] = useState(null);
  const [docView, setDocView] = useState(null); // { doc, variant }
  const [me, setMe] = useState(applicant);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);

  useEffect(() => { setMe(applicant); }, [applicant]);

  const refresh = useCallback(async () => {
    if (!me?.id) return;
    try {
      const [m, d, a, itv] = await Promise.all([
        api(`/api/admin/applicants/${me.id}/messages`),
        api(`/api/admin/applicants/${me.id}/documents`),
        api(`/api/admin/applicants/${me.id}/audit`),
        api(`/api/admin/applicants/${me.id}/interviews`),
      ]);
      setMessages(m.messages || []); setDocuments(d.documents || []); setAudit(a.entries || []);
      setInterviews(itv.interviews || []);
    } catch (e) { toast.error(e.message); }
  }, [me?.id, api]);
  useEffect(() => { refresh(); }, [refresh]);

  const reloadMe = async () => {
    try { const r = await api(`/api/applicants/${me.id}`); setMe(r); onChanged?.(r); } catch {}
  };

  const loadTestReview = useCallback(async () => {
    if (!me?.id) return;
    setTestLoading(true);
    try {
      const data = await api(`/api/admin/applicants/${me.id}/test-review`);
      setTestReview(data);
      const mr = data.manual_review || {};
      const auto = data.auto_score || {};
      setTestOverride({
        band: mr.band || auto.band || '',
        recommendation: mr.recommendation || auto.recommendation || '',
        manual_notes: mr.manual_notes || '',
      });
    } catch (e) { setTestReview({ error: e.message }); }
    finally { setTestLoading(false); }
  }, [me?.id, api]);
  useEffect(() => { if (tab === 'test' && !testReview) loadTestReview(); }, [tab, testReview, loadTestReview]);

  const saveTestOverride = async () => {
    try {
      await api(`/api/admin/applicants/${me.id}/test-review`, { method: 'PATCH', body: JSON.stringify(testOverride) });
      toast.success('Manual grade saved');
      await loadTestReview();
      await reloadMe();
    } catch (e) { toast.error(e.message); }
  };

  const action = async (fn, labelDone) => {
    try { await fn(); toast.success(labelDone); await refresh(); await reloadMe(); }
    catch (e) { toast.error(e.message); }
  };

  const setStage = (stage) => action(
    () => api(`/api/applicants/${me.id}/stage`, { method: 'PATCH', body: JSON.stringify({ stage }) }),
    `Moved to ${stage.replace(/_/g, ' ')}`
  );
  const doDisable = () => action(() => api(`/api/admin/applicants/${me.id}/disable`, { method: 'POST' }), 'Account disabled');
  const doEnable  = () => action(() => api(`/api/admin/applicants/${me.id}/enable`,  { method: 'POST' }), 'Account enabled');
  const doReset   = () => action(async () => {
    const r = await api(`/api/admin/applicants/${me.id}/reset-password`, { method: 'POST', body: JSON.stringify({ notify_applicant: true }) });
    if (r?.temp_password) toast.message('New temp password', { description: r.temp_password, duration: 10000 });
  }, 'Password reset');
  const doResendPw = () => action(async () => {
    const r = await api(`/api/admin/applicants/${me.id}/resend-temp-password`, { method: 'POST' });
    if (r?.temp_password) toast.message('Resent temp password', { description: r.temp_password, duration: 10000 });
  }, 'Temporary password resent');
  const reviewDoc = (doc_id, decision, correction) => action(
    () => api(`/api/admin/applicants/${me.id}/documents/${doc_id}`, { method: 'PATCH', body: JSON.stringify({ decision, correction_notice: correction || null }) }),
    `Document ${decision === 'approve' ? 'approved' : 'rejected'}`
  );
  const cancelDoc = (doc_id) => action(
    () => api(`/api/admin/applicants/${me.id}/documents/${doc_id}`, { method: 'DELETE' }),
    'Document cancelled'
  );

  // ---- ONBOARDING DOC LIBRARY · assign-from-library dialog state ----
  const [libraryAssignOpen, setLibraryAssignOpen] = useState(false);

  // ---- PHASE 5 · PROMOTE TO HIRED ----
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promotionStatus, setPromotionStatus] = useState(null);
  const loadPromotionStatus = useCallback(async () => {
    if (!me?.id) return;
    try {
      const data = await api(`/api/admin/applicants/${me.id}/promotion-status`);
      setPromotionStatus(data);
    } catch (e) { /* ignore */ }
  }, [me?.id, api]);
  useEffect(() => { loadPromotionStatus(); }, [loadPromotionStatus]);

  // ---- INTERVIEW CRUD ----
  const cancelInterview = (interview_id) => action(
    () => api(`/api/admin/applicants/${me.id}/interviews/${interview_id}`, { method: 'DELETE' }),
    'Interview cancelled'
  );
  const remindInterview = (interview_id) => action(
    () => api(`/api/admin/applicants/${me.id}/interviews/${interview_id}/remind`, { method: 'POST' }),
    'Reminder email sent'
  );

  if (!me) return null;
  const disabled = me.account_status === 'DISABLED' || me.portal_active === false;

  // iOS Safari fix: do not close on overlay tap — only on explicit X button.
  // The overlay is non-interactive; closing happens via the X in the header.
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex justify-end text-slate-900" data-testid="applicant-detail-drawer">
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto text-slate-900" onClick={e => e.stopPropagation()}>
        {/* Sticky header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 z-10">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-xl font-heading font-bold !text-slate-900" data-testid="applicant-name">{me.first_name} {me.last_name}</h2>
              <p className="text-xs font-mono !text-slate-700">{me.id} • {me.position_applied}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge className="bg-amber-300 text-amber-950 border-amber-500 border">{(me.stage || '').replace(/_/g, ' ')}</Badge>
                {disabled ? (
                  <Badge className="bg-rose-200 text-rose-900 border-rose-400 border">Portal Disabled</Badge>
                ) : (
                  <Badge className="bg-emerald-200 text-emerald-900 border-emerald-400 border">Portal Active</Badge>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-600 hover:text-slate-900 text-xl leading-none" data-testid="drawer-close">✕</button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Action bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-slate-50 border border-slate-200 rounded p-3" data-testid="admin-action-bar">
            <ActionBtn icon={Edit3} label="Edit account" onClick={() => setEditOpen(true)} testId="act-edit" />
            <ActionBtn icon={KeyRound} label="Reset password" onClick={doReset} testId="act-reset" />
            <ActionBtn icon={Send} label="Resend temp pw" onClick={doResendPw} testId="act-resend" />
            <ActionBtn icon={MessageSquare} label="Send message" onClick={() => setMsgOpen(true)} testId="act-message" />
            <ActionBtn icon={Mail} label="Send notice" onClick={() => setNoticeOpen(true)} tone="amber" testId="act-notice" />
            <ActionBtn icon={FileUp} label="Assign document" onClick={() => setDocOpen(true)} tone="amber" testId="act-doc" />
            <ActionBtn icon={ClipboardList} label="Schedule interview" onClick={() => { setEditInterview(null); setScheduleOpen(true); }} tone="amber" testId="act-interview" />
            {disabled ? (
              <ActionBtn icon={Power} label="Re-enable account" onClick={doEnable} tone="emerald" testId="act-enable" />
            ) : (
              <ActionBtn icon={PowerOff} label="Disable account" onClick={doDisable} tone="rose" testId="act-disable" />
            )}
            <ActionBtn icon={Archive} label="Archive / delete" onClick={() => setDeleteOpen(true)} tone="rose" testId="act-delete" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="stage" data-testid="tab-stage">Stage</TabsTrigger>
              <TabsTrigger value="decision" data-testid="tab-decision">Decision</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">Documents {documents.length ? `(${documents.length})` : ''}</TabsTrigger>
              <TabsTrigger value="interviews" data-testid="tab-interviews">Interviews {interviews.length ? `(${interviews.length})` : ''}</TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages">Communications {messages.length ? `(${messages.length})` : ''}</TabsTrigger>
              <TabsTrigger value="test" data-testid="tab-test">Test Review</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
            </TabsList>

            {/* OVERVIEW --------------------------------------------------- */}
            <TabsContent value="overview" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <InfoRow label="Email" value={me.email} />
                <InfoRow label="Phone" value={me.phone} />
                <InfoRow label="Location" value={[me.city, me.state].filter(Boolean).join(', ')} />
                <InfoRow label="Date of birth" value={me.date_of_birth} />
                <InfoRow label="DL" value={me.drivers_license_number ? `${me.drivers_license_number} (${me.drivers_license_state || ''})` : null} />
                <InfoRow label="PI license" value={me.pi_license_number ? `${me.pi_license_number} (${me.pi_license_state || ''})` : null} />
                <InfoRow label="Experience" value={me.prior_experience_years != null ? `${me.prior_experience_years} yrs` : null} />
                <InfoRow label="Prior agency" value={me.prior_agency} />
                <InfoRow label="Assigned reviewer" value={me.assigned_reviewer_id} />
                <InfoRow label="Assigned interviewer" value={me.assigned_interviewer_id} />
                <InfoRow label="Interview outcome" value={me.interview_outcome} />
                <InfoRow label="Onboarding" value={me.onboarding_progress} />
              </div>
              {me.why_interested && (
                <div className="bg-slate-50 border border-slate-200 rounded p-3">
                  <p className="text-xs font-mono uppercase tracking-wider text-slate-700 mb-1">Why Nexus</p>
                  <p className="text-sm text-slate-900 whitespace-pre-wrap">{me.why_interested}</p>
                </div>
              )}
              {me.test_score && (
                <div className="border border-emerald-400 bg-emerald-50 rounded p-4" data-testid="applicant-test-score-panel">
                  <h3 className="text-sm font-heading font-semibold text-slate-900 mb-2">IPS-OIE Assessment Result</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <InfoRow label="Raw" value={`${me.test_score.raw_score}/${me.test_score.max_raw_score}`} />
                    <InfoRow label="Weighted" value={`${me.test_score.weighted_pct}%`} />
                    <InfoRow label="Band" value={me.test_score.band} />
                    <InfoRow label="Recommendation" value={me.test_score.recommendation} />
                  </div>
                  {me.test_score.red_flags?.length > 0 && (
                    <div className="mt-3 text-xs text-rose-800">
                      <p className="font-mono uppercase tracking-wider mb-1">Red flags</p>
                      <ul className="space-y-0.5">{me.test_score.red_flags.map((f,i)=>(<li key={i}>• [{f.severity}] {f.reason}</li>))}</ul>
                    </div>
                  )}
                </div>
              )}
              {me.admin_notes?.length > 0 && (
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-slate-700 mb-2">Admin notes (internal)</p>
                  <ul className="space-y-2">
                    {me.admin_notes.map(n => (
                      <li key={n.id} className="border border-slate-300 rounded p-2 text-sm bg-slate-50">
                        <p className="text-slate-900">{n.note}</p>
                        <p className="text-[10px] font-mono text-slate-600 mt-1">{n.by_name} • {new Date(n.at).toLocaleString()}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* FULL SUBMITTED APPLICATION (Unified Profile — Section 3) */}
              <FullApplicationAccordion applicant={me} />
            </TabsContent>

            {/* STAGE ------------------------------------------------------ */}
            <TabsContent value="stage" className="pt-4 space-y-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-slate-700 mb-2">Pipeline stage</p>
                <p className="text-[11px] text-slate-600 mb-3">
                  Current: <span className="font-semibold text-slate-900">{bucketForStage(me.stage).label}</span>
                  {' · '}<span className="text-slate-500">{granularLabel(me.stage)}</span>
                </p>
                {/* Compact dropdown (≤120px vertical) replacing the 16-button stack */}
                <div className="max-w-md">
                  <Select
                    value={bucketForStage(me.stage).id}
                    onValueChange={(bid) => {
                      const b = STAGE_BUCKETS.find(x => x.id === bid);
                      if (b && bucketForStage(me.stage).id !== b.id) setStage(b.entry);
                    }}
                  >
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900 h-11" data-testid="bucket-select">
                      <SelectValue placeholder="Move applicant to…" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGE_BUCKETS.map(b => (
                        <SelectItem key={b.id} value={b.id} data-testid={`bucket-option-${b.id}`}>
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                            {b.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Terminal decisions */}
              <div className="grid sm:grid-cols-2 gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => setStage('HIRED')} data-testid="convert-hire">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Convert to New Hire / Investigator
                </Button>
                <Button size="sm" variant="outline" className="border-rose-400 text-rose-800 hover:bg-rose-50" onClick={() => setStage('NOT_HIRED')} data-testid="mark-not-hired">
                  <XCircle className="w-4 h-4 mr-1.5" /> Mark Not Hired
                </Button>
              </div>

              {/* Advanced fine-grained jump (collapsed) */}
              <details className="rounded border border-slate-300 bg-slate-50">
                <summary className="cursor-pointer px-3 py-2 text-xs font-mono uppercase tracking-wider text-slate-700 select-none">
                  Advanced: granular sub-stages
                </summary>
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {STAGE_META.filter(s => s.key !== me.stage).map(s => (
                    <Button key={s.key} variant="outline" size="sm"
                      className="justify-start text-xs h-9 border-slate-300 text-slate-900 hover:bg-amber-50 hover:border-amber-400"
                      onClick={() => setStage(s.key)} data-testid={`stage-btn-${s.key}`}>
                      <ChevronRight className="w-3.5 h-3.5 mr-1.5" /> {s.label}
                    </Button>
                  ))}
                </div>
              </details>
            </TabsContent>

            {/* DOCUMENTS -------------------------------------------------- */}
            <TabsContent value="documents" className="pt-4 space-y-3" data-testid="documents-tab-content">
              <div className="rounded-md border border-slate-300 bg-slate-50 p-3 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row" data-testid="assign-from-library-banner">
                <div className="text-sm text-slate-800 min-w-0">
                  <p className="font-semibold text-slate-900">Assign from your document library</p>
                  <p className="text-xs text-slate-700">Pick any onboarding template you've already uploaded and assign it to this applicant.</p>
                </div>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold flex-shrink-0 w-full sm:w-auto" onClick={() => setLibraryAssignOpen(true)} data-testid="open-library-assign-btn" style={{ minHeight: '40px' }}>
                  <FileText className="w-4 h-4 mr-1.5" /> Pick from library
                </Button>
              </div>

              {documents.length === 0 && <p className="text-sm text-slate-600 italic">No documents assigned.</p>}
              {documents.map(d => {
                const statusChip = (() => {
                  const s = d.status;
                  if (s === 'APPROVED') return { label: 'APPROVED', cls: 'bg-emerald-200 text-emerald-900 border-emerald-500' };
                  if (s === 'SUBMITTED') return { label: 'Signed · Review', cls: 'bg-blue-200 text-blue-900 border-blue-500' };
                  if (s === 'REJECTED') return { label: 'Rejected', cls: 'bg-rose-200 text-rose-900 border-rose-500' };
                  if (s === 'CANCELLED') return { label: 'Cancelled', cls: 'bg-slate-200 text-slate-800 border-slate-400' };
                  return { label: d.required ? 'Required' : 'Pending', cls: 'bg-amber-200 text-amber-900 border-amber-500' };
                })();
                return (
                  <div key={d.id} className="border border-slate-300 rounded p-3 text-sm bg-white" data-testid={`doc-row-${d.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          <FileText className="w-4 h-4 text-slate-700 flex-shrink-0" />
                          <span className="truncate">{d.label}</span>
                          <span className={`inline-flex items-center rounded border px-1.5 text-[10px] font-mono uppercase tracking-wider truncate ${statusChip.cls}`} style={{ height: '24px', maxWidth: '180px' }} data-testid={`doc-chip-${d.id}`}>
                            {statusChip.label}
                          </span>
                          {d.require_signature && <span className="inline-flex items-center rounded border border-purple-400 bg-purple-100 text-purple-900 px-1.5 text-[10px] font-mono uppercase" style={{ height: '24px' }}>Sig req</span>}
                        </p>
                        <p className="text-xs text-slate-700 mt-0.5">Category: {d.category} • Due: {d.due_date || '—'}</p>
                        {d.admin_file_name && <p className="text-xs text-slate-700 mt-0.5">Template: {d.admin_file_name}</p>}
                        {d.submitted_file_name && <p className="text-xs text-emerald-800 mt-0.5">Applicant upload: {d.submitted_file_name}</p>}
                        {d.submission_signature && <p className="text-xs text-slate-700 mt-0.5">Signed as: {d.submission_signature}</p>}
                        {d.correction_notice && <p className="text-xs text-rose-800 mt-1 bg-rose-50 border border-rose-200 rounded p-1.5">Correction: {d.correction_notice}</p>}
                      </div>
                      <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                        {d.admin_file_key && (
                          <Button size="sm" variant="outline" className="border-slate-400 text-slate-900 h-8 text-xs" onClick={() => setDocView({ doc: d, variant: 'admin' })} data-testid={`doc-view-template-${d.id}`}>
                            <FileText className="w-3 h-3 mr-1" /> View template
                          </Button>
                        )}
                        {d.submitted_file_key && (
                          <Button size="sm" variant="outline" className="border-blue-400 text-blue-900 h-8 text-xs" onClick={() => setDocView({ doc: d, variant: 'submission' })} data-testid={`doc-view-submission-${d.id}`}>
                            <FileText className="w-3 h-3 mr-1" /> View signed
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Admin action bar */}
                    {(d.status === 'SUBMITTED' || d.status !== 'CANCELLED') && (
                      <div className="mt-2.5 pt-2.5 border-t border-slate-200 flex flex-wrap gap-1.5" data-testid={`doc-actionbar-${d.id}`}>
                        {d.status === 'SUBMITTED' && (
                          <>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs" onClick={() => reviewDoc(d.id, 'approve')} data-testid={`doc-approve-${d.id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Approve document
                            </Button>
                            <Button size="sm" variant="outline" className="border-rose-400 text-rose-800 h-8 text-xs" onClick={() => {
                              const note = window.prompt('Correction notice for applicant:'); if (note) reviewDoc(d.id, 'reject', note);
                            }} data-testid={`doc-reject-${d.id}`}>
                              <XCircle className="w-3 h-3 mr-1" /> Reject / Re-sign
                            </Button>
                          </>
                        )}
                        {d.status !== 'CANCELLED' && (
                          <Button size="sm" variant="outline" className="border-slate-300 text-slate-800 h-8 text-xs ml-auto" onClick={() => cancelDoc(d.id)} data-testid={`doc-cancel-${d.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Cancel
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900" onClick={() => setDocOpen(true)} data-testid="new-doc-btn">
                <Plus className="w-4 h-4 mr-1" /> Assign another document
              </Button>
            </TabsContent>

            {/* INTERVIEWS ------------------------------------------------- */}
            <TabsContent value="interviews" className="pt-4 space-y-3" data-testid="interviews-tab-content">
              {interviews.length === 0 && <p className="text-sm text-slate-600 italic">No interviews scheduled yet.</p>}
              {interviews.map(itv => (
                <div key={itv.id} className="border border-slate-300 rounded p-3 text-sm bg-white" data-testid={`interview-row-${itv.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">
                          {itv.interview_type?.replace(/_/g, ' ')} interview
                        </p>
                        <Badge className={`text-[10px] border ${
                          itv.status === 'CANCELLED' ? 'bg-slate-200 text-slate-800 border-slate-400' :
                          itv.status === 'COMPLETED' ? (
                            itv.outcome === 'PASS' ? 'bg-emerald-200 text-emerald-900 border-emerald-400' :
                            itv.outcome === 'FAIL' ? 'bg-rose-200 text-rose-900 border-rose-400' :
                            'bg-amber-200 text-amber-900 border-amber-400'
                          ) :
                          'bg-purple-200 text-purple-900 border-purple-400'
                        }`}>
                          {itv.status}{itv.outcome ? ` • ${itv.outcome}` : ''}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-700 mt-0.5">
                        When: <span className="font-semibold">{itv.scheduled_at}</span>
                        {` • ${itv.duration_minutes || 45} min`}
                        {itv.interviewer_name ? ` • with ${itv.interviewer_name}` : ''}
                      </p>
                      {itv.location && (
                        <p className="text-xs text-slate-700 mt-0.5 break-all">
                          {itv.interview_type === 'VIDEO' ? 'Meeting link: ' : 'Location: '}
                          {itv.interview_type === 'VIDEO' && /^https?:\/\//.test(itv.location) ? (
                            <a href={itv.location} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{itv.location}</a>
                          ) : itv.location}
                        </p>
                      )}
                      {itv.notes && <p className="text-xs text-slate-700 mt-0.5">Notes: {itv.notes}</p>}
                      {itv.decision_notes && <p className="text-xs text-slate-700 mt-1 bg-slate-50 border border-slate-200 rounded p-1.5">Outcome notes: {itv.decision_notes}</p>}
                      {itv.invite_email_status && <p className="text-[10px] text-slate-500 mt-1">Invite email: {itv.invite_email_status}</p>}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {itv.status === 'SCHEDULED' && (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs" onClick={() => setOutcomeInterview(itv)} data-testid={`interview-outcome-${itv.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Record outcome
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-300 text-slate-900 h-7 text-xs" onClick={() => { setEditInterview(itv); setScheduleOpen(true); }} data-testid={`interview-edit-${itv.id}`}>
                            <Edit3 className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 h-7 text-xs" onClick={() => remindInterview(itv.id)} data-testid={`interview-remind-${itv.id}`}>
                            <Mail className="w-3 h-3 mr-1" /> Remind
                          </Button>
                          <Button size="sm" variant="outline" className="border-rose-400 text-rose-800 h-7 text-xs" onClick={() => cancelInterview(itv.id)} data-testid={`interview-cancel-${itv.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900" onClick={() => { setEditInterview(null); setScheduleOpen(true); }} data-testid="schedule-interview-btn">
                <Plus className="w-4 h-4 mr-1" /> Schedule interview
              </Button>
            </TabsContent>

            {/* COMMUNICATIONS --------------------------------------------- */}
            <TabsContent value="messages" className="pt-4 space-y-3">
              {messages.length === 0 && <p className="text-sm text-slate-600 italic">No messages sent yet.</p>}
              {messages.map(m => (
                <div key={m.id} className="border border-slate-300 rounded p-3 text-sm" data-testid={`msg-row-${m.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{m.subject}</p>
                      <p className="text-[11px] text-slate-700">By {m.from_name} • {new Date(m.sent_at).toLocaleString()} • delivery: {m.delivery}{m.email_status ? ` • email: ${m.email_status}` : ''}</p>
                    </div>
                    <Badge className={`text-[10px] border ${m.read_at ? 'bg-emerald-100 text-emerald-900 border-emerald-400' : 'bg-slate-200 text-slate-900 border-slate-400'}`}>
                      {m.read_at ? 'Read' : 'Unread'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-900 mt-2 whitespace-pre-wrap">{m.body}</p>
                </div>
              ))}
              <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900" onClick={() => setMsgOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> New message
              </Button>
            </TabsContent>

            {/* TEST REVIEW ------------------------------------------------ */}
            <TabsContent value="test" className="pt-4 space-y-3">
              {testLoading && <p className="text-sm text-slate-600 italic">Loading test…</p>}
              {testReview?.error && <p className="text-sm text-rose-800 bg-rose-50 border border-rose-300 rounded p-3">{testReview.error}</p>}
              {testReview && !testReview.error && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 border border-slate-200 rounded p-3" data-testid="test-auto-summary">
                    <div><p className="text-[10px] font-mono uppercase !text-slate-700">Raw</p><p className="text-base font-bold !text-slate-900">{testReview.auto_score?.raw_score}/{testReview.auto_score?.max_raw_score}</p></div>
                    <div><p className="text-[10px] font-mono uppercase !text-slate-700">Weighted</p><p className="text-base font-bold !text-slate-900">{testReview.auto_score?.weighted_pct}%</p></div>
                    <div><p className="text-[10px] font-mono uppercase !text-slate-700">Auto Band</p><p className="text-base font-bold !text-slate-900">{testReview.auto_score?.band || '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase !text-slate-700">Auto Recommendation</p><p className="text-base font-bold !text-slate-900">{testReview.auto_score?.recommendation || '—'}</p></div>
                  </div>

                  <div className="border border-amber-400 bg-amber-50 rounded p-3 space-y-2" data-testid="test-manual-override">
                    <p className="text-xs font-mono uppercase tracking-wider !text-slate-800 font-semibold">Manual override (self-grading)</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="!text-slate-800 text-xs">Band</Label>
                        <Select value={testOverride.band} onValueChange={v => setTestOverride(p => ({...p, band: v}))}>
                          <SelectTrigger className="bg-white" data-testid="override-band"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>{['Strongly Recommended','Recommended','Conditional','Not Recommended','Disqualified'].map(b=>(<SelectItem key={b} value={b}>{b}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="!text-slate-800 text-xs">Recommendation</Label>
                        <Select value={testOverride.recommendation} onValueChange={v => setTestOverride(p => ({...p, recommendation: v}))}>
                          <SelectTrigger className="bg-white" data-testid="override-rec"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>{['Advance','Conditional advance','Hold','Reject'].map(b=>(<SelectItem key={b} value={b}>{b}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label className="!text-slate-800 text-xs">Notes (internal)</Label>
                      <Textarea rows={2} value={testOverride.manual_notes} onChange={e=>setTestOverride(p=>({...p, manual_notes: e.target.value}))} data-testid="override-notes" /></div>
                    <Button size="sm" onClick={saveTestOverride} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="override-save">Save manual grade</Button>
                    {testReview.manual_review?.reviewed_by_name && (
                      <p className="text-[10px] font-mono !text-slate-700">Last reviewed by {testReview.manual_review.reviewed_by_name} • {new Date(testReview.manual_review.reviewed_at).toLocaleString()}</p>
                    )}
                  </div>

                  <div className="border border-slate-300 rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold !text-slate-900">#</th>
                          <th className="text-left px-2 py-1.5 font-semibold !text-slate-900">Section</th>
                          <th className="text-left px-2 py-1.5 font-semibold !text-slate-900">Question</th>
                          <th className="text-left px-2 py-1.5 font-semibold !text-slate-900">Given</th>
                          <th className="text-left px-2 py-1.5 font-semibold !text-slate-900">Key</th>
                          <th className="text-left px-2 py-1.5 font-semibold !text-slate-900">Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testReview.rows.map(r => (
                          <tr key={r.number} className="border-t border-slate-200 align-top" data-testid={`test-row-${r.number}`}>
                            <td className="px-2 py-1.5 font-mono !text-slate-900">{r.number}</td>
                            <td className="px-2 py-1.5 !text-slate-700 text-xs">{r.section}</td>
                            <td className="px-2 py-1.5 !text-slate-900 text-xs">{r.prompt}</td>
                            <td className="px-2 py-1.5 font-mono font-bold !text-slate-900">{r.given || '—'}</td>
                            <td className="px-2 py-1.5 font-mono !text-slate-700">{r.correct}</td>
                            <td className="px-2 py-1.5">{r.matched
                              ? <Badge className="bg-emerald-200 text-emerald-900 border border-emerald-400 text-[10px]">✓</Badge>
                              : <Badge className="bg-rose-200 text-rose-900 border border-rose-400 text-[10px]">✗</Badge>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* DECISION (Phase 5) ----------------------------------------- */}
            <TabsContent value="decision" className="pt-4 space-y-4" data-testid="decision-tab-content">
              {!promotionStatus ? (
                <p className="text-sm text-slate-600 italic">Loading promotion status…</p>
              ) : promotionStatus.already_promoted ? (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 space-y-2" data-testid="already-promoted-card">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-emerald-700" />
                    <h3 className="font-semibold text-emerald-900">Already promoted to investigator account</h3>
                  </div>
                  {promotionStatus.promoted_user ? (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div><dt className="text-[10px] font-mono uppercase text-slate-600">Username</dt><dd className="text-slate-900 font-mono">{promotionStatus.promoted_user.username}</dd></div>
                      <div><dt className="text-[10px] font-mono uppercase text-slate-600">User ID</dt><dd className="text-slate-900 font-mono">{promotionStatus.promoted_user.id}</dd></div>
                      <div><dt className="text-[10px] font-mono uppercase text-slate-600">Role</dt><dd className="text-slate-900">{promotionStatus.promoted_user.role}</dd></div>
                      <div><dt className="text-[10px] font-mono uppercase text-slate-600">Promoted at</dt><dd className="text-slate-900 text-xs">{promotionStatus.promoted_at ? new Date(promotionStatus.promoted_at).toLocaleString() : '—'}</dd></div>
                    </dl>
                  ) : (
                    <p className="text-xs text-slate-700">User account record not found — please contact support.</p>
                  )}
                </div>
              ) : (
                <>
                  {/* Pre-flight checklist */}
                  <div className="rounded-md border border-slate-300 bg-white p-4 space-y-2">
                    <p className="text-xs font-mono uppercase tracking-wider text-slate-700">Pre-flight checklist</p>
                    <ul className="space-y-1.5 text-sm">
                      <li className="flex items-start gap-2" data-testid="checklist-interview">
                        {promotionStatus.passed_interview
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          : <XCircle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />}
                        <span className={promotionStatus.passed_interview ? 'text-slate-900' : 'text-slate-700'}>
                          Interview passed
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-testid="checklist-docs">
                        {promotionStatus.required_docs_outstanding === 0
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          : <XCircle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />}
                        <span className={promotionStatus.required_docs_outstanding === 0 ? 'text-slate-900' : 'text-slate-700'}>
                          All required documents approved
                          {promotionStatus.required_docs_outstanding > 0 && (
                            <span className="block text-xs text-rose-700 mt-0.5">
                              Outstanding ({promotionStatus.required_docs_outstanding}): {promotionStatus.required_docs_outstanding_labels.join(', ')}
                            </span>
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Decision actions */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      size="lg"
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex-1"
                      onClick={() => setPromoteOpen(true)}
                      data-testid="promote-to-hired-btn"
                      style={{ minHeight: '48px' }}
                    >
                      <Award className="w-4 h-4 mr-2" /> Promote to investigator account
                      {!promotionStatus.ready_to_promote && (
                        <span className="ml-2 text-[10px] font-mono uppercase bg-amber-400/90 text-amber-950 px-1.5 py-0.5 rounded">Force</span>
                      )}
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-rose-400 text-rose-800 hover:bg-rose-50 flex-1"
                      onClick={() => setStage('NOT_HIRED')}
                      data-testid="decision-not-hired-btn"
                      style={{ minHeight: '48px' }}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Mark not hired
                    </Button>
                  </div>
                  {!promotionStatus.ready_to_promote && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded p-2">
                      Pre-flight checks are incomplete. Promotion will still proceed if you confirm — Nexus admins can override.
                    </p>
                  )}
                </>
              )}
            </TabsContent>
            <TabsContent value="audit" className="pt-4 space-y-2">
              {audit.length === 0 && <p className="text-sm text-slate-600 italic">No audit entries yet.</p>}
              {audit.map(a => (
                <div key={a.id} className="border-l-4 border-amber-400 bg-slate-50 pl-3 py-2 text-xs" data-testid={`audit-row-${a.id}`}>
                  <p className="font-semibold text-slate-900">{a.action}{a.reason ? ` — ${a.reason}` : ''}</p>
                  <p className="text-slate-700">{a.by_name} • {new Date(a.at).toLocaleString()}</p>
                  {a.details && Object.keys(a.details).length > 0 && (
                    <pre className="mt-1 text-[10px] text-slate-700 whitespace-pre-wrap">{JSON.stringify(a.details, null, 0)}</pre>
                  )}
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <EditApplicantDialog open={editOpen} onClose={() => setEditOpen(false)} applicant={me} onSaved={() => { reloadMe(); refresh(); }} />
      <DeleteApplicantDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} applicant={me} onDeleted={() => { onChanged?.(null); onClose?.(); }} />
      <SendMessageDialog open={msgOpen} onClose={() => setMsgOpen(false)} applicant={me} onSent={refresh} />
      <SendNoticeDialog open={noticeOpen} onClose={() => setNoticeOpen(false)} applicant={me} onSent={refresh} />
      <AssignDocumentDialog open={docOpen} onClose={() => setDocOpen(false)} applicant={me} onSent={refresh} />
      <InterviewDialog open={scheduleOpen} onClose={() => { setScheduleOpen(false); setEditInterview(null); }} applicant={me} editing={editInterview} onSaved={refresh} />
      <InterviewOutcomeDialog open={!!outcomeInterview} onClose={() => setOutcomeInterview(null)} applicant={me} interview={outcomeInterview} onSaved={() => { refresh(); reloadMe(); }} />
      <DocumentViewerDialog open={!!docView} onClose={() => setDocView(null)} applicant={me} docInfo={docView} />
      <PromoteToHiredDialog open={promoteOpen} onClose={() => setPromoteOpen(false)} applicant={me} onPromoted={() => { reloadMe(); loadPromotionStatus(); refresh(); }} />
      <LibraryAssignDialog open={libraryAssignOpen} onClose={() => setLibraryAssignOpen(false)} applicant={me} onAssigned={refresh} />
    </div>
  );
};

// ============================== Library Assign Dialog =======================
const LibraryAssignDialog = ({ open, onClose, applicant, onAssigned }) => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    if (!open || !applicant?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api('/api/admin/onboarding-templates');
        if (alive) setTemplates(r.templates || []);
      } catch (e) { toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, applicant?.id, api]);

  const assign = async (tpl) => {
    setSubmitting(tpl.id);
    try {
      await api(`/api/admin/applicants/${applicant.id}/documents/from-template/${tpl.id}`, {
        method: 'POST',
        body: JSON.stringify({ required: true }),
      });
      toast.success(`Assigned "${tpl.label}" to applicant`);
      onAssigned?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white text-slate-900 max-w-2xl w-[95vw] max-h-[85vh] flex flex-col" data-testid="library-assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Pick a document template</DialogTitle>
          <p className="text-xs text-slate-600 mt-1">Each pick creates a fresh, applicant-scoped copy of the template.</p>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pt-2">
          {loading && <p className="text-sm text-slate-600 italic">Loading templates…</p>}
          {!loading && templates.length === 0 && (
            <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="library-empty">
              <FileText className="w-7 h-7 text-slate-400 mx-auto mb-1.5" />
              <p className="text-sm font-semibold text-slate-700">No templates uploaded yet</p>
              <p className="text-xs text-slate-500 mt-1">Go to the <span className="font-mono">Document Library</span> tab to upload reusable onboarding files.</p>
            </div>
          )}
          {templates.map(tpl => (
            <div key={tpl.id} className="border border-slate-300 rounded p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`library-row-${tpl.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 truncate">{tpl.label}</p>
                <p className="text-[11px] text-slate-600 truncate">{tpl.category} · {tpl.file_name} · {Math.round((tpl.file_size || 0) / 1024)} KB</p>
                {tpl.description && <p className="text-xs text-slate-700 mt-1">{tpl.description}</p>}
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {tpl.require_signature && <span className="inline-flex items-center rounded bg-purple-100 text-purple-900 border border-purple-300 px-1.5 text-[10px] font-mono uppercase">Sig req</span>}
                  {tpl.require_upload_back && <span className="inline-flex items-center rounded bg-blue-100 text-blue-900 border border-blue-300 px-1.5 text-[10px] font-mono uppercase">Upload back</span>}
                </div>
              </div>
              <Button size="sm" onClick={() => assign(tpl)} disabled={submitting === tpl.id} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold flex-shrink-0" data-testid={`library-assign-${tpl.id}`}>
                {submitting === tpl.id ? 'Assigning…' : 'Assign'}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={onClose} className="border-slate-300 text-slate-900">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================== Document Library Tab =======================
const OnboardingLibraryPanel = () => {
  const { api } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ label: '', category: 'general', description: '', require_signature: false, require_upload_back: false, default_due_days: 7 });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('/api/admin/onboarding-templates');
      setTemplates(r.templates || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { refresh(); }, [refresh]);

  const submitUpload = async () => {
    if (!uploadForm.label.trim() || !uploadFile) { toast.error('Label and file are required.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('label', uploadForm.label.trim());
      fd.append('category', uploadForm.category || 'general');
      fd.append('description', uploadForm.description || '');
      fd.append('require_signature', String(uploadForm.require_signature));
      fd.append('require_upload_back', String(uploadForm.require_upload_back));
      fd.append('default_due_days', String(uploadForm.default_due_days || 7));
      fd.append('file', uploadFile);
      const res = await fetch(`${BACKEND_URL}/api/admin/onboarding-templates`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Upload failed (${res.status})`);
      toast.success('Template uploaded');
      setUploadOpen(false);
      setUploadForm({ label: '', category: 'general', description: '', require_signature: false, require_upload_back: false, default_due_days: 7 });
      setUploadFile(null);
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.label}" from the library?\n\nAlready-assigned applicant copies will NOT be affected.`)) return;
    try {
      await api(`/api/admin/onboarding-templates/${t.id}`, { method: 'DELETE' });
      toast.success('Template removed');
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const view = async (t) => {
    try {
      const url = `${BACKEND_URL}/api/admin/onboarding-templates/${t.id}/view`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      // Don't revoke immediately — let the new window keep the URL alive
    } catch (e) { toast.error(`Unable to open: ${e.message}`); }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-library-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Onboarding document library</h2>
          <p className="text-xs text-slate-600">Upload reusable PDFs, contracts, and policy forms. Assign any of them to new hires with one click.</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="library-upload-btn" style={{ minHeight: '40px' }}>
          <FileUp className="w-4 h-4 mr-1.5" /> Upload template
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-600 italic">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="library-empty-state">
          <FileText className="w-9 h-9 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700">No templates yet</p>
          <p className="text-xs text-slate-500 mt-1">Upload your first onboarding document so you can re-send it to every new hire.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="library-grid">
          {templates.map(t => (
            <div key={t.id} className="rounded-md border-2 border-slate-300 bg-white p-3" data-testid={`library-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{t.label}</p>
                  <p className="text-[11px] text-slate-600 truncate">{t.category} · {t.file_name} · {Math.round((t.file_size || 0) / 1024)} KB</p>
                </div>
              </div>
              {t.description && <p className="text-xs text-slate-700 mt-1.5 line-clamp-2">{t.description}</p>}
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {t.require_signature && <span className="inline-flex items-center rounded bg-purple-100 text-purple-900 border border-purple-300 px-1.5 text-[10px] font-mono uppercase" style={{ height: '20px' }}>Sig req</span>}
                {t.require_upload_back && <span className="inline-flex items-center rounded bg-blue-100 text-blue-900 border border-blue-300 px-1.5 text-[10px] font-mono uppercase" style={{ height: '20px' }}>Upload back</span>}
                <span className="inline-flex items-center rounded bg-slate-100 text-slate-800 border border-slate-300 px-1.5 text-[10px] font-mono uppercase" style={{ height: '20px' }}>Due {t.default_due_days}d</span>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => view(t)} className="border-slate-300 text-slate-900" data-testid={`library-view-${t.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(t)} className="border-rose-400 text-rose-700 hover:bg-rose-50" data-testid={`library-delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 font-mono">Uploaded {t.uploaded_at ? new Date(t.uploaded_at).toLocaleDateString() : ''} by {t.uploaded_by_name || '—'}</p>
            </div>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(v) => !v && setUploadOpen(false)}>
        <DialogContent className="bg-white text-slate-900 max-w-lg" data-testid="library-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Upload onboarding template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Label *</Label>
              <Input value={uploadForm.label} onChange={e => setUploadForm({ ...uploadForm, label: e.target.value })} placeholder="e.g. Use of Force Agreement" className="bg-white border-slate-300 text-slate-900" data-testid="upload-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Input value={uploadForm.category} onChange={e => setUploadForm({ ...uploadForm, category: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="upload-category" />
              </div>
              <div>
                <Label className="text-xs">Default due (days)</Label>
                <Input type="number" min={1} max={90} value={uploadForm.default_due_days} onChange={e => setUploadForm({ ...uploadForm, default_due_days: Number(e.target.value) || 7 })} className="bg-white border-slate-300 text-slate-900" data-testid="upload-due" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea rows={2} value={uploadForm.description} onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })} className="bg-white border-slate-300 text-slate-900 resize-none" data-testid="upload-desc" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs text-slate-800">
                <Checkbox checked={uploadForm.require_signature} onCheckedChange={(v) => setUploadForm({ ...uploadForm, require_signature: !!v })} data-testid="upload-sig" /> Requires signature
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-800">
                <Checkbox checked={uploadForm.require_upload_back} onCheckedChange={(v) => setUploadForm({ ...uploadForm, require_upload_back: !!v })} data-testid="upload-back" /> Requires upload back
              </label>
            </div>
            <div>
              <Label className="text-xs">File * <span className="text-slate-500 font-normal">(PDF / DOCX / image, ≤ 20 MB)</span></Label>
              <Input type="file" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="bg-white border-slate-300 text-slate-900" data-testid="upload-file" />
              {uploadFile && <p className="text-[10px] text-slate-600 mt-1">{uploadFile.name} · {Math.round(uploadFile.size / 1024)} KB</p>}
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="border-slate-300 text-slate-900">Cancel</Button>
            <Button onClick={submitUpload} disabled={uploading} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="upload-submit">
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="text-sm">
    <span className="!text-slate-700 font-medium">{label}:</span>{' '}
    <span className="!text-slate-900 font-semibold">{value || '—'}</span>
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, tone = 'slate', testId }) => (
  <button type="button" onClick={onClick}
    className={`inline-flex items-center justify-start text-xs h-9 px-3 rounded border font-semibold transition-colors ${
      tone === 'rose' ? 'border-rose-500 !text-rose-900 bg-white hover:bg-rose-50' :
      tone === 'amber' ? 'border-amber-500 !text-amber-950 bg-white hover:bg-amber-50' :
      tone === 'emerald' ? 'border-emerald-500 !text-emerald-900 bg-white hover:bg-emerald-50' :
      'border-slate-400 !text-slate-900 bg-white hover:bg-slate-100'
    }`}
    data-testid={testId}>
    <Icon className="w-3.5 h-3.5 mr-1.5" /> <span className="truncate">{label}</span>
  </button>
);

// ============================ Portal Accounts Tab ===========================
const PortalAccountsTab = ({ applicants, onSelect, onRefresh }) => {
  const [search, setSearch] = useState('');
  const filtered = (applicants || []).filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (a.name || '').toLowerCase().includes(s) || (a.email || '').toLowerCase().includes(s) || (a.id || '').toLowerCase().includes(s);
  });
  return (
    <div data-testid="portal-accounts-tab">
      <div className="flex items-center gap-3 mb-4">
        <Input placeholder="Search by name, email, or ID" value={search} onChange={e=>setSearch(e.target.value)} className="max-w-md" data-testid="accounts-search" />
        <Button variant="outline" onClick={onRefresh} className="border-slate-300 !text-slate-900"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-900">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Applicant</th>
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="text-left px-3 py-2 font-semibold">Stage</th>
              <th className="text-left px-3 py-2 font-semibold">Test</th>
              <th className="text-left px-3 py-2 font-semibold">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => (
              <tr key={a.id} className="border-t border-slate-200 hover:bg-amber-50 cursor-pointer" onClick={() => onSelect(a.id)} data-testid={`accounts-row-${a.id}`}>
                <td className="px-3 py-2">
                  <p className="text-slate-900 font-semibold">{a.name || '(unnamed)'}</p>
                  <p className="text-xs font-mono text-slate-700">{a.id}</p>
                </td>
                <td className="px-3 py-2 text-slate-900">{a.email}</td>
                <td className="px-3 py-2"><Badge className="bg-amber-200 text-amber-950 border border-amber-400 text-[10px]">{(a.stage || '').replace(/_/g,' ')}</Badge></td>
                <td className="px-3 py-2 text-slate-900">{a.test_score != null ? `${a.test_score}%` : '—'}</td>
                <td className="px-3 py-2 text-slate-700">{a.last_activity_at ? new Date(a.last_activity_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-600 italic">No applicants match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ============================ Document Viewer ================================

// ============================ Promote-to-Hired Dialog (Phase 5) ============
const PromoteToHiredDialog = ({ open, onClose, applicant, onPromoted }) => {
  const { api } = useAuth();
  const [f, setF] = useState({
    role: 'field_investigator',
    title: '',
    department: '',
    badge_number: '',
    send_handbook_email: true,
    require_password_change: true,
    day_one_auto_assign: true,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {username, initial_password, ...}
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setResult(null); setErr(''); }
  }, [open]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const data = await api(`/api/admin/applicants/${applicant.id}/promote-to-hired`, {
        method: 'POST',
        body: JSON.stringify(f),
      });
      setResult(data);
      onPromoted?.();
      toast.success(`Promoted — ${data.username} is now a ${f.role.replace(/_/g, ' ')}.`);
    } catch (e) {
      setErr(e.message || 'Failed to promote');
    } finally {
      setBusy(false);
    }
  };

  const copyCreds = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(`Nexus credentials\nUsername: ${result.username}\nInitial password: ${result.initial_password}`);
      toast.success('Credentials copied');
    } catch { toast.error('Clipboard unavailable'); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && !busy && onClose?.()}>
      <DialogContent className="bg-white text-slate-900 max-w-lg" data-testid="promote-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-emerald-600" /> Promote to investigator account
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="promote-result">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3">
              <p className="text-sm font-semibold text-emerald-900">Account created.</p>
              <p className="text-xs text-emerald-800">The applicant can sign in immediately. {result.email_status === 'sent' ? 'A branded welcome email with the handbook link has been delivered.' : 'Email was not sent — share the credentials below out-of-band.'}</p>
            </div>
            <dl className="space-y-2">
              <div>
                <dt className="text-[10px] font-mono uppercase text-slate-600">Username</dt>
                <dd className="font-mono text-slate-900 select-all" data-testid="promote-username">{result.username}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-mono uppercase text-slate-600">Initial password (shown once)</dt>
                <dd className="font-mono text-slate-900 select-all bg-slate-100 px-2 py-1 rounded border border-slate-300" data-testid="promote-initial-password">{result.initial_password}</dd>
              </div>
            </dl>
            {result.day_one?.enabled && (
              <div className="rounded-md border border-blue-300 bg-blue-50 p-3 space-y-1" data-testid="promote-day-one-result">
                <p className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Day-One Auto-Assign
                </p>
                <ul className="text-xs text-blue-900 space-y-0.5 ml-4 list-disc">
                  {result.day_one.training_case_id ? (
                    <li>Enrolled in training case <span className="font-mono">{result.day_one.training_case_number || result.day_one.training_case_id}</span></li>
                  ) : (
                    <li>No training case is configured — skip silently. Mark a case <code>is_training_case=true</code> to auto-enrol new hires.</li>
                  )}
                  {result.day_one.supervisor_name ? (
                    <li>Supervisor <span className="font-semibold">{result.day_one.supervisor_name}</span> notified ({result.day_one.supervisor_email_status || 'queued'})</li>
                  ) : (
                    <li>No supervisor found to notify.</li>
                  )}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copyCreds} className="border-slate-300 text-slate-900" data-testid="promote-copy-creds">
                <Copy className="w-4 h-4 mr-1" /> Copy credentials
              </Button>
              <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-slate-800 text-xs font-mono uppercase">Role</Label>
              <Select value={f.role} onValueChange={v => setF({ ...f, role: v })}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="promote-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="field_investigator">Field Investigator</SelectItem>
                  <SelectItem value="skip_tracer">Skip Tracer</SelectItem>
                  <SelectItem value="transporter">Transporter</SelectItem>
                  <SelectItem value="case_manager">Case Manager</SelectItem>
                  <SelectItem value="department_admin">Department Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-800 text-xs font-mono uppercase">Title</Label>
                <Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="promote-title" />
              </div>
              <div>
                <Label className="text-slate-800 text-xs font-mono uppercase">Department</Label>
                <Input value={f.department} onChange={e => setF({ ...f, department: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="promote-department" />
              </div>
            </div>
            <div>
              <Label className="text-slate-800 text-xs font-mono uppercase">Badge #</Label>
              <Input value={f.badge_number} onChange={e => setF({ ...f, badge_number: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="promote-badge" />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <Checkbox checked={f.send_handbook_email} onCheckedChange={v => setF({ ...f, send_handbook_email: !!v })} data-testid="promote-send-email" />
                Send branded welcome / employee handbook email
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <Checkbox checked={f.require_password_change} onCheckedChange={v => setF({ ...f, require_password_change: !!v })} data-testid="promote-require-pwchange" />
                Require password change on first sign-in
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <Checkbox checked={f.day_one_auto_assign} onCheckedChange={v => setF({ ...f, day_one_auto_assign: !!v })} className="mt-0.5" data-testid="promote-day-one-auto" />
                <span>
                  <span className="font-medium">Day-One Auto-Assign</span>
                  <span className="block text-xs text-slate-600">Add to training case (if configured) and email their supervisor so day-one onboarding starts immediately.</span>
                </span>
              </label>
            </div>
            {err && (
              <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900" data-testid="promote-error">
                {err}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-300 text-slate-900">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="promote-submit">
                {busy ? (<><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Promoting…</>) : (<><Award className="w-4 h-4 mr-1" /> Confirm promotion</>)}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


const DocumentViewerDialog = ({ open, onClose, applicant, docInfo }) => {
  const token = localStorage.getItem('nexus_token');
  const [blobUrl, setBlobUrl] = useState(null);
  const [mime, setMime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !docInfo || !applicant?.id) return;
    let alive = true;
    let createdUrl = null;
    (async () => {
      setLoading(true); setError(''); setBlobUrl(null);
      try {
        const url = `${BACKEND_URL}/api/admin/applicants/${applicant.id}/documents/${docInfo.doc.id}/view?variant=${docInfo.variant}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error(
              docInfo.variant === 'submission'
                ? "Applicant hasn't uploaded a signed version yet."
                : "No template file was uploaded for this document — it may have only required a typed signature."
            );
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        createdUrl = URL.createObjectURL(blob);
        if (alive) { setBlobUrl(createdUrl); setMime(blob.type); }
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load file');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, docInfo, applicant?.id, token]);

  if (!docInfo) return null;
  const isImg = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf';
  const fileName = docInfo.variant === 'submission' ? docInfo.doc.submitted_file_name : docInfo.doc.admin_file_name;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
      <DialogContent className="bg-white text-slate-900 max-w-4xl w-[95vw] max-h-[90vh] flex flex-col" data-testid="document-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 truncate">
            {docInfo.variant === 'submission' ? 'Signed submission' : 'Document template'} — {docInfo.doc.label}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto bg-slate-100 rounded border border-slate-300">
          {loading && <p className="p-6 text-center text-slate-700">Loading…</p>}
          {error && <p className="p-6 text-center text-rose-700">Unable to load: {error}</p>}
          {!loading && !error && blobUrl && (
            isPdf ? (
              <iframe src={blobUrl} title={fileName || 'document'} className="w-full h-[70vh] bg-white" data-testid="doc-viewer-pdf" />
            ) : isImg ? (
              <img src={blobUrl} alt={fileName} className="w-full h-auto" data-testid="doc-viewer-img" />
            ) : (
              <div className="p-6 text-center space-y-3">
                <p className="text-sm text-slate-700">Preview not available for this file type ({mime || 'unknown'}).</p>
                <a href={blobUrl} download={fileName || 'document'} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="doc-viewer-download">
                  <Download className="w-4 h-4" /> Download {fileName}
                </a>
              </div>
            )
          )}
        </div>
        {blobUrl && (
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <a href={blobUrl} download={fileName || 'document'} className="text-sm text-slate-700 hover:text-slate-900 underline" data-testid="doc-viewer-download-link">
              Download original
            </a>
            <Button variant="outline" onClick={onClose} className="border-slate-300 text-slate-900">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================ Full Application Accordion ====================
const _hasVal = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
const _fmtVal = (v) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const _FIELD_SECTIONS = [
  { title: 'Personal demographics', fields: [
    ['First name', 'first_name'], ['Middle name', 'middle_name'], ['Last name', 'last_name'],
    ['Date of birth', 'date_of_birth'], ['SSN (last 4)', 'ssn_last4'], ['Gender', 'gender'],
    ['Ethnicity', 'ethnicity'], ['Marital status', 'marital_status'],
    ['Email', 'email'], ['Phone', 'phone'], ['Alternate phone', 'alt_phone'],
    ['Street address', 'address'], ['Apt/Unit', 'address_2'],
    ['City', 'city'], ['State', 'state'], ['ZIP', 'zip'],
  ]},
  { title: 'Identification', fields: [
    ["Driver's license #", 'drivers_license_number'], ["Driver's license state", 'drivers_license_state'],
    ["Driver's license expiry", 'drivers_license_expiration'],
    ['PI license #', 'pi_license_number'], ['PI license state', 'pi_license_state'],
    ['PI license expiry', 'pi_license_expiration'],
    ['Citizenship', 'citizenship'], ['Work authorization', 'work_authorization'],
    ['Veteran', 'veteran'], ['Veteran branch', 'veteran_branch'], ['DD-214 on file', 'dd214_on_file'],
  ]},
  { title: 'Position & availability', fields: [
    ['Position applied', 'position_applied'], ['Position other', 'position_other'],
    ['Desired pay', 'desired_pay'], ['Available start date', 'available_start_date'],
    ['Employment type', 'employment_type'], ['Willing to travel', 'willing_to_travel'],
    ['Willing to relocate', 'willing_to_relocate'], ['Has reliable vehicle', 'has_reliable_vehicle'],
    ['Vehicle year/make/model', 'vehicle_info'], ['Vehicle insurance', 'has_vehicle_insurance'],
  ]},
  { title: 'Education', fields: [
    ['Highest level', 'highest_education'], ['School name', 'school_name'],
    ['Field of study', 'field_of_study'], ['Graduation year', 'graduation_year'],
  ]},
  { title: 'Professional experience', fields: [
    ['Years of experience', 'prior_experience_years'],
    ['Most recent agency', 'prior_agency'],
    ['Title at last agency', 'prior_agency_title'],
    ['Dates at last agency', 'prior_agency_dates'],
    ['Supervisor name', 'prior_supervisor_name'],
    ['Supervisor phone', 'prior_supervisor_phone'],
    ['May we contact?', 'may_contact_prior_supervisor'],
    ['Reason for leaving', 'prior_reason_for_leaving'],
    ['Past investigations summary', 'past_investigations_summary'],
  ]},
  { title: 'References (with consent)', fields: [
    ['Reference 1', 'reference_1'], ['Reference 2', 'reference_2'], ['Reference 3', 'reference_3'],
  ]},
  { title: 'Skills, certifications & training', fields: [
    ['Skills', 'skills'], ['Certifications', 'certifications'], ['Training', 'training'],
    ['Languages', 'languages'], ['Firearms training', 'firearms_training'],
    ['Firearms permit #', 'firearms_permit_number'],
    ['CCW state', 'ccw_state'], ['Self-defense certifications', 'self_defense_certifications'],
  ]},
  { title: 'Background disclosure', fields: [
    ['Felony conviction', 'felony_conviction'], ['Felony details', 'felony_details'],
    ['Misdemeanor conviction', 'misdemeanor_conviction'], ['Misdemeanor details', 'misdemeanor_details'],
    ['Pending charges', 'pending_charges'], ['Pending charges details', 'pending_charges_details'],
    ['Civil judgments', 'civil_judgments'], ['Bankruptcies', 'bankruptcies'],
    ['Restraining orders', 'restraining_orders'],
    ['Disciplinary action at prior employer', 'prior_employer_discipline'],
  ]},
  { title: 'Disclosures & consents', fields: [
    ['Consents to background check', 'background_consent'],
    ['Consents to drug testing', 'drug_test_consent'],
    ['Consents to driving record', 'driving_record_consent'],
    ['Consents to credit check', 'credit_check_consent'],
    ['Acknowledges at-will', 'at_will_acknowledged'],
    ['Acknowledges arbitration', 'arbitration_acknowledged'],
    ['Read employee handbook', 'handbook_acknowledged'],
  ]},
  { title: 'Statement & signature', fields: [
    ['Why interested in Nexus', 'why_interested'],
    ['Statement of truthfulness', 'truthfulness_statement'],
    ['Typed signature', 'signature_typed_name'],
    ['Signature date', 'signature_date'],
    ['IP address (signing)', 'signature_ip'],
  ]},
];

const FullApplicationAccordion = ({ applicant }) => {
  const [openIds, setOpenIds] = useState(() => new Set(['Personal demographics']));

  const populated = _FIELD_SECTIONS.map(sec => ({
    ...sec,
    rows: sec.fields
      .map(([label, key]) => [label, applicant?.[key]])
      .filter(([, v]) => _hasVal(v)),
  })).filter(s => s.rows.length > 0);

  const knownKeys = new Set(_FIELD_SECTIONS.flatMap(s => s.fields.map(f => f[1])));
  const adminInternal = new Set([
    'id', 'company_id', 'created_at', 'updated_at', 'last_activity_at',
    'stage', 'admin_notes', 'audit_log', 'portal_username', 'portal_password_hash',
    'portal_account_id', 'require_password_change', 'risk_flag', 'test_score',
    'submitted_documents', '_id', 'name', 'assigned_reviewer_id', 'assigned_interviewer_id',
    'onboarding_progress', 'interview_outcome', 'temp_password', 'archived', 'archived_at',
    'archived_by_user_id', 'archived_reason', 'created_by_user_id',
  ]);
  const extraRows = Object.entries(applicant || {})
    .filter(([k, v]) => !knownKeys.has(k) && !adminInternal.has(k) && _hasVal(v) && typeof v !== 'object')
    .map(([k, v]) => [k.replace(/_/g, ' '), v]);
  if (extraRows.length > 0) {
    populated.push({ title: 'Other submitted fields', rows: extraRows });
  }

  const toggle = (id) => {
    setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  if (populated.length === 0) {
    return (
      <div className="border border-slate-300 rounded p-4 bg-slate-50 text-sm text-slate-600" data-testid="full-application-empty">
        No application form responses on file yet.
      </div>
    );
  }

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="full-application-accordion">
      <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-300 text-xs font-mono uppercase tracking-[0.18em] text-slate-800 font-semibold">
        Complete submitted application
      </div>
      {populated.map(sec => {
        const isOpen = openIds.has(sec.title);
        const id = sec.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return (
          <div key={sec.title} className="border-b border-slate-200 last:border-b-0">
            <button
              type="button"
              onClick={() => toggle(sec.title)}
              data-testid={`accordion-${id}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500"
              style={{ minHeight: '44px' }}
              aria-expanded={isOpen}
            >
              <span className="text-sm font-semibold text-slate-900">{sec.title}</span>
              <span className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] font-mono text-slate-600">{sec.rows.length} field{sec.rows.length === 1 ? '' : 's'}</span>
                <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </span>
            </button>
            {isOpen && (
              <dl className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid={`accordion-body-${id}`}>
                {sec.rows.map(([label, value]) => (
                  <div key={label} className="flex flex-col py-1.5 border-b border-slate-100 last:border-b-0 sm:border-b-0">
                    <dt className="text-[10px] font-mono uppercase tracking-wider text-slate-600">{label}</dt>
                    <dd className="text-sm text-slate-900 break-words whitespace-pre-wrap">{_fmtVal(value)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ============================== Onboarding Progress (Phase 6) ===============
const OnboardingProgressTab = ({ onSelect, onPromote, refreshSignal }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api('/api/admin/onboarding-progress');
      setData(r);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load, refreshSignal]);

  const totals = data?.totals || { in_progress: 0, ready_to_promote: 0, promoted: 0 };
  const rows = data?.applicants || [];

  const barColor = (pct, ready) => {
    if (ready) return 'bg-emerald-500';
    if (pct >= 75) return 'bg-emerald-400';
    if (pct >= 40) return 'bg-amber-400';
    return 'bg-slate-400';
  };

  return (
    <div className="space-y-4" data-testid="onboarding-progress-panel">
      {/* Totals header */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-md border-2 border-slate-300 bg-white p-3" data-testid="onboarding-total-in-progress">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">In progress</p>
          <p className="text-2xl font-bold text-slate-900 mt-0.5">{totals.in_progress}</p>
        </div>
        <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3" data-testid="onboarding-total-ready">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-800">Ready to promote</p>
          <p className="text-2xl font-bold text-amber-900 mt-0.5 flex items-center gap-2">
            {totals.ready_to_promote}
            {totals.ready_to_promote > 0 && <Sparkles className="w-5 h-5 text-amber-500" />}
          </p>
        </div>
        <div className="rounded-md border-2 border-emerald-400 bg-emerald-50 p-3" data-testid="onboarding-total-promoted">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-800">Promoted</p>
          <p className="text-2xl font-bold text-emerald-900 mt-0.5">{totals.promoted}</p>
        </div>
      </div>

      {/* Refresh + state */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-700">
          {loading ? 'Loading…' : `${rows.length} candidate${rows.length === 1 ? '' : 's'} in onboarding`}
        </p>
        <Button variant="outline" size="sm" onClick={load} className="border-slate-300 text-slate-900" data-testid="onboarding-refresh-btn" style={{ minHeight: '36px' }}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900" data-testid="onboarding-error">
          {error}
        </div>
      )}

      {/* Rows */}
      {!loading && rows.length === 0 && !error && (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="onboarding-empty">
          <Award className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700">No active onboarding candidates</p>
          <p className="text-xs text-slate-500 mt-1">
            Candidates appear here once they pass the interview phase and have a hiring packet assigned.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {rows.map(r => (
          <div
            key={r.applicant_id}
            className="rounded-md border-2 border-slate-300 bg-white p-3 hover:border-amber-400 transition-colors"
            data-testid={`onboarding-row-${r.applicant_id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-900 truncate">{r.name}</p>
                  <span className="inline-flex items-center rounded bg-slate-200 text-slate-800 border border-slate-300 px-1.5 text-[10px] font-mono uppercase tracking-wider" style={{ height: '20px' }}>
                    {granularLabel(r.stage)}
                  </span>
                  {r.passed_interview && (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-100 text-emerald-900 border border-emerald-400 px-1.5 text-[10px] font-mono uppercase tracking-wider" style={{ height: '20px' }} data-testid={`onboarding-interview-pass-${r.applicant_id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Interview passed
                    </span>
                  )}
                  {r.already_promoted && (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-200 text-emerald-950 border border-emerald-500 px-1.5 text-[10px] font-mono uppercase tracking-wider" style={{ height: '20px' }} data-testid={`onboarding-promoted-${r.applicant_id}`}>
                      <Award className="w-3 h-3" /> Promoted
                    </span>
                  )}
                  {r.ready_to_promote && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-200 text-amber-950 border border-amber-500 px-1.5 text-[10px] font-mono uppercase tracking-wider" style={{ height: '20px' }} data-testid={`onboarding-ready-${r.applicant_id}`}>
                      <Sparkles className="w-3 h-3" /> Ready
                    </span>
                  )}
                </div>
                {r.position && <p className="text-[11px] text-slate-600 mt-0.5 truncate">{r.position}</p>}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelect(r.applicant_id)}
                  className="border-slate-300 text-slate-900"
                  data-testid={`onboarding-open-${r.applicant_id}`}
                  style={{ minHeight: '36px' }}
                >
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Open
                </Button>
                {r.ready_to_promote && (
                  <Button
                    size="sm"
                    onClick={() => onPromote(r.applicant_id)}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                    data-testid={`onboarding-promote-${r.applicant_id}`}
                    style={{ minHeight: '36px' }}
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Promote
                  </Button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-2.5">
              <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                <span className="text-slate-700">
                  Packet {r.docs_approved}/{r.docs_total} approved
                  {r.docs_submitted > 0 && <span className="text-slate-500"> · {r.docs_submitted} awaiting review</span>}
                  {r.docs_pending > 0 && <span className="text-slate-500"> · {r.docs_pending} pending</span>}
                </span>
                <span className="font-bold text-slate-900" data-testid={`onboarding-pct-${r.applicant_id}`}>{r.completion_pct}%</span>
              </div>
              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${barColor(r.completion_pct, r.ready_to_promote)} transition-all`}
                  style={{ width: `${r.completion_pct}%` }}
                />
              </div>
            </div>

            {/* Outstanding required */}
            {!r.already_promoted && r.outstanding_required_count > 0 && (
              <div className="mt-2 flex items-start gap-1.5 text-[11px]" data-testid={`onboarding-outstanding-${r.applicant_id}`}>
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-slate-700">
                  <span className="font-semibold">{r.outstanding_required_count} required outstanding:</span>{' '}
                  <span className="text-slate-600">{(r.outstanding_required_labels || []).join(' · ')}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// ============================== Main page ====================================
const NexusNewHire = () => {
  const { api } = useAuth();
  const [pipeline, setPipeline] = useState({ groups: {}, counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedInitialTab, setSelectedInitialTab] = useState('overview');
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState('pipeline');
  const [onboardingRefresh, setOnboardingRefresh] = useState(0);

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    try { setPipeline(await api('/api/applicants/pipeline')); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const openApplicant = async (id, initialTab = 'overview') => {
    try {
      const data = await api(`/api/applicants/${id}`);
      setSelectedInitialTab(initialTab);
      setSelected(data);
    } catch (e) { toast.error(e.message); }
  };

  const allApplicants = useMemo(() => Object.values(pipeline.groups || {}).flat(), [pipeline]);

  // Group applicants into the 5 consolidated buckets (Section 4 of blueprint)
  const bucketedApplicants = useMemo(() => {
    const out = Object.fromEntries(STAGE_BUCKETS.map(b => [b.id, []]));
    const terminalRows = [];
    Object.entries(pipeline.groups || {}).forEach(([stageKey, rows]) => {
      const isTerminal = TERMINAL_BUCKET.keys.includes(stageKey);
      if (isTerminal) { terminalRows.push(...rows.map(r => ({ ...r, _stage: stageKey }))); return; }
      const b = bucketForStage(stageKey);
      (out[b.id] = out[b.id] || []).push(...rows.map(r => ({ ...r, _stage: stageKey })));
    });
    return { buckets: out, terminal: terminalRows };
  }, [pipeline.groups]);

  return (
    <div className="p-3 sm:p-5 max-w-[1800px] mx-auto flex flex-col" style={{ minHeight: 0 }} data-testid="nexus-new-hire-page">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <img src="/nexus-patch.png" alt="Nexus" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
            <span className="truncate">Nexus Recruitment Center</span>
          </h1>
          <p className="text-[10px] sm:text-xs text-slate-700 font-mono uppercase tracking-[0.22em] mt-1 truncate">
            Company Admin Control Center — {pipeline.total} applicant{pipeline.total === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" onClick={fetchPipeline} className="border-slate-300 text-slate-900" data-testid="refresh-pipeline" style={{ minHeight: '44px' }}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" onClick={() => setCreateOpen(true)} data-testid="open-create-applicant" style={{ minHeight: '44px' }}>
            <UserPlus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Create applicant</span>
          </Button>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="flex flex-col flex-1 min-h-0">
        <TabsList className="bg-slate-100 flex-shrink-0">
          <TabsTrigger value="pipeline" data-testid="view-pipeline"><ClipboardList className="w-4 h-4 mr-1" /> Pipeline Board</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="view-onboarding"><Award className="w-4 h-4 mr-1" /> Onboarding</TabsTrigger>
          <TabsTrigger value="library" data-testid="view-library"><FileText className="w-4 h-4 mr-1" /> Document Library</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="view-accounts"><UserPlus className="w-4 h-4 mr-1" /> Portal Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="pt-3 flex-1 min-h-0 flex flex-col" data-testid="pipeline-board-container">
          {/* 5-bucket counts micro-stepper (mobile-first horizontal) */}
          <div className="flex gap-2 overflow-x-auto pb-2 flex-shrink-0" data-testid="bucket-stepper">
            {STAGE_BUCKETS.map((b, i) => {
              const count = bucketedApplicants.buckets[b.id]?.length || 0;
              return (
                <div key={b.id} className="flex items-center gap-2 flex-shrink-0">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${b.tone} text-[11px] font-mono uppercase tracking-wider`} style={{ height: '28px' }}>
                    <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                    <span>{b.short}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                  {i < STAGE_BUCKETS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                </div>
              );
            })}
          </div>

          {/* 5-bucket Kanban — flex-locked column-based grid, vertical scroll INSIDE each column */}
          <div
            className="grid gap-3 flex-1 min-h-0 overflow-x-auto"
            style={{ gridTemplateColumns: 'repeat(5, minmax(220px, 1fr))', gridAutoRows: '1fr' }}
            data-testid="pipeline-board"
          >
            {STAGE_BUCKETS.map(b => {
              const rows = bucketedApplicants.buckets[b.id] || [];
              return (
                <div key={b.id} className="flex flex-col min-h-0" data-testid={`bucket-column-${b.id}`}>
                  <div className={`px-3 py-2 border-2 rounded-t-md text-[11px] font-mono uppercase tracking-wider font-semibold flex items-center justify-between flex-shrink-0 ${b.tone}`}>
                    <span className="truncate">{b.label}</span>
                    <span className="opacity-75 ml-2 flex-shrink-0">{rows.length}</span>
                  </div>
                  <div className="bg-slate-50 border-x-2 border-b-2 border-slate-300 rounded-b-md p-2 space-y-1.5 overflow-y-auto flex-1 min-h-0">
                    {rows.length === 0 ? (
                      <p className="text-[11px] text-slate-500 text-center py-4 italic">empty</p>
                    ) : rows.map(r => (
                      <button key={r.id} onClick={() => openApplicant(r.id)}
                        className="w-full text-left bg-white border border-slate-300 rounded px-2.5 py-2 hover:border-amber-500 hover:shadow-md transition-all active:bg-amber-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500"
                        data-testid={`applicant-card-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 truncate">{r.name || '(unnamed)'}</p>
                            <p className="text-[11px] text-slate-700 truncate">{r.position}</p>
                          </div>
                          {r.risk_flag && (
                            <span className="inline-flex items-center gap-0.5 rounded bg-rose-200 text-rose-900 border border-rose-400 px-1.5 text-[10px] font-mono uppercase truncate flex-shrink-0" style={{ maxHeight: '24px', maxWidth: '90px' }}>
                              <AlertTriangle className="w-3 h-3" />
                              <span className="truncate">{r.risk_flag}</span>
                            </span>
                          )}
                        </div>
                        {/* 24px granular status chip with ellipsis */}
                        <div className="mt-1.5 flex items-center justify-between gap-1.5">
                          <span
                            className="inline-flex items-center rounded bg-slate-200 text-slate-800 border border-slate-300 px-1.5 text-[10px] font-mono uppercase tracking-wider truncate"
                            style={{ height: '20px', maxWidth: '70%' }}
                            title={granularLabel(r._stage)}
                          >
                            {granularLabel(r._stage)}
                          </span>
                          {r.test_score != null && (
                            <span className="text-[10px] font-mono font-bold text-emerald-700 flex-shrink-0">{r.test_score}%</span>
                          )}
                        </div>
                        {r.last_activity_at && (
                          <div className="mt-1 text-[9px] text-slate-500 font-mono flex items-center gap-1 truncate">
                            <Clock className="w-2.5 h-2.5 flex-shrink-0" /> {new Date(r.last_activity_at).toLocaleDateString()}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {bucketedApplicants.terminal.length > 0 && (
            <details className="mt-3 flex-shrink-0 rounded border border-slate-300 bg-slate-50">
              <summary className="cursor-pointer px-3 py-2 text-xs font-mono uppercase tracking-wider text-slate-700">
                Closed ({bucketedApplicants.terminal.length}) — Not Hired / Archived
              </summary>
              <div className="p-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {bucketedApplicants.terminal.map(r => (
                  <button key={r.id} onClick={() => openApplicant(r.id)}
                    className="text-left bg-white border border-slate-300 rounded px-2 py-1.5 hover:border-slate-500"
                    data-testid={`terminal-card-${r.id}`}>
                    <p className="text-xs font-semibold text-slate-900 truncate">{r.name || '(unnamed)'}</p>
                    <p className="text-[10px] text-slate-600 truncate">{granularLabel(r._stage)}</p>
                  </button>
                ))}
              </div>
            </details>
          )}
        </TabsContent>

        <TabsContent value="onboarding" className="pt-3 flex-1 min-h-0 overflow-y-auto" data-testid="onboarding-tab-container">
          <OnboardingProgressTab
            refreshSignal={onboardingRefresh}
            onSelect={(id) => openApplicant(id, 'documents')}
            onPromote={(id) => openApplicant(id, 'decision')}
          />
        </TabsContent>

        <TabsContent value="library" className="pt-3 flex-1 min-h-0 overflow-y-auto" data-testid="library-tab-container">
          <OnboardingLibraryPanel />
        </TabsContent>

        <TabsContent value="accounts" className="pt-4">
          <PortalAccountsTab applicants={allApplicants} onSelect={openApplicant} onRefresh={fetchPipeline} />
        </TabsContent>
      </Tabs>

      {selected && <ApplicantDetail applicant={selected} initialTab={selectedInitialTab} onClose={() => { setSelected(null); setOnboardingRefresh(x => x + 1); }} onChanged={(a) => { if (a) setSelected(a); fetchPipeline(); setOnboardingRefresh(x => x + 1); }} />}
      <CreateApplicantDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => fetchPipeline()} />
    </div>
  );
};

export default NexusNewHire;
